// #6602: Gift Transfer's tranche engine -- pure helpers shared by the Gift Transfer modal,
// the Tranche Scan and the shared tranche table. No DOM, no module state.
import { balanceTolerance, nearlyEqualBalance, isLLProducts, isTrancheEligibleSecurityType } from './client-flags.js';
import { getEffectiveDenomination } from './denomination-resolver.js';
import { resolveDescriptionTemplate } from './string-utils.js';
import {
	listTransactionCustomColumns,
	usesTransactionCustomColumnKeyStorage,
	isCustomColumnApplicableToClass,
	getCustomColumnAutoPopulateValue,
} from './transaction-custom-column-value.js';
import { TransactionColumnRegistry, isSenderBasisEnabled } from './TransactionColumnRegistry.js';

export function isNA(val) {
	const s = String(val ?? '')
		.trim()
		.toLowerCase();
	return s === 'n/a' || s === 'n.a.' || s === 'na' || s === 'n.a';
}

export function isKnownNumber(val) {
	if (val === null || val === undefined) return false;
	if (typeof val === 'string') {
		const s = val.trim().toLowerCase();
		if (s === '' || isNA(s)) return false;
	}
	return !isNaN(Number(val));
}

/**
 * #6602: notes and carryover Tax Basis for a gift row drawn from one source tranche.
 * Pure -- cert numbering stays with the caller, which passes the cert it reserved.
 *
 * @param {Object} source - remaining tranche (giftTransactions row)
 * @param {Object} opts
 * @param {Object} opts.clientData
 * @param {boolean} [opts.buyBack] - Buy-Back/Redemption mode
 * @param {boolean} [opts.toIssuer] - recipient is the Issuer
 * @param {boolean} [opts.certColumn] - client tracks certificate numbers
 * @param {string} [opts.nextCert] - cert reserved for this row (certColumn only)
 * @param {string} [opts.buyBackSenderName] - set for isBB clients: "Gift from {sender}" notes
 * @param {string} [opts.labelBB]
 * @returns {{notes: string, taxBasis: string}}
 */
export function buildGiftCarryover(source, opts = {}) {
	const { clientData, buyBack = false, toIssuer = false, certColumn = false, nextCert, buyBackSenderName, labelBB = 'Buy-Back' } = opts;
	const sourceNotes = String(source.notes ?? '').trim();
	const sourceDate = moment.unix(Number(source.date)).utc().format('M/D/YY');

	let notes = source.certNumber ? labelBB + ' (' + source.certNumber + ')' : labelBB + ' (' + sourceNotes + ' dated ' + sourceDate + ')';

	if (!buyBack) {
		if (!toIssuer) {
			if (certColumn) {
				const sourceCert = source.certNumber ? source.certNumber.replace(/^0+/, '') : '';
				notes = !nextCert
					? 'Transfer'
					: sourceCert
						? resolveDescriptionTemplate(clientData, 'gift_cert_transfer', { nextCert, sourceCert }, 'Transfer ({nextCert} from {sourceCert})')
						: resolveDescriptionTemplate(clientData, 'gift_cert_transfer_no_source', { nextCert }, 'Transfer ({nextCert})');
			} else {
				notes = resolveDescriptionTemplate(
					clientData,
					'gift_notes_transfer',
					{ sourceNotes, sourceDate },
					'Transfer ({sourceNotes} dated {sourceDate})'
				);
			}
		}
		if (buyBackSenderName) {
			const firstWord = String(source.notes ?? '').split(' ')[0];
			const dateStart =
				Number.isInteger(Date.parse(firstWord)) ||
				['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'].some((m) => firstWord.toLowerCase().startsWith(m));
			notes = resolveDescriptionTemplate(
				clientData,
				'gift_from_buyback',
				{ senderName: buyBackSenderName, sourceDate, dateSeparator: dateStart ? '| ' : '', sourceNotes },
				'Gift from {senderName} - {sourceDate} {dateSeparator}{sourceNotes}'
			);
		}
	}

	let taxBasis = '';
	if (isKnownNumber(source.taxBasis)) taxBasis = Number(source.taxBasis).toString();
	else if (isKnownNumber(source._effectiveTaxBasis)) taxBasis = Number(source._effectiveTaxBasis).toString();

	return { notes, taxBasis };
}

// #6144: which matching signal decided a Historic Holdings/Prior Transfer row --
// feeds the "Keys" legend under the Prior Tranches table (Stepwise Analysis).
export const MATCH_KEY_NAMES = {
	A: 'Explicit Cert Match',
	B: 'Notes Text Match',
	C: 'Perfect Fit',
	D: 'Split Across Tranches',
	E: 'Best-Guess Match',
	F: 'Basis Carried Forward',
	G: 'Sender Basis Match',
	H: 'Date Match (from Notes)',
	I: 'Only Possibility',
};
export const MATCH_KEY_DESCS = {
	A: 'Linked by an exact certificate/lot number referenced in the notes.',
	B: 'Linked by a free-text description in the notes.',
	C: "One prior holding exactly covers this transfer's quantity.",
	D: 'Multiple prior holdings were combined to exactly cover this transfer.',
	E: 'No explicit cert/notes link; the closest candidate by quantity, date, and price was inferred.',
	F: 'Share Price/Tax Basis was inherited from the matched holding rather than recorded on this transaction.',
	G: "The sender-declared basis exactly matches this candidate holding's recorded Tax Basis.",
	H: 'A date written in the notes is within 5 days of the actual transaction date on one side of the match.',
	I: 'Exactly one eligible holding existed at all.',
};
// Short mnemonic shown in the badge/legend -- avoids arbitrary single letters.
export const MATCH_KEY_LABELS = { A: 'EC', B: 'NM', C: 'PF', D: 'ST', E: 'BG', F: 'BC', G: 'SB', H: 'DM', I: 'OP' };
// #6144b: confidence tier per Key, matches Nth Round's Tranche-Analysis-Explained.pdf exactly --
// Only Possibility (violet, top tier): I, no other eligible candidate existed at all -- a stronger,
// categorically different certainty than any textual evidence, since evidence can be mis-recorded
// but "there was nothing else it could be" can't. Checked first in every branch that assigns it and
// overrides everything else, including tagMatch's own sender-basis ('G') promotion below.
// Confirmed (green): A/B/G direct evidence. Certain (blue): C/D/H, the numbers fit only one way.
// Inferred (red): E, best-available estimate. F (Basis Carried Forward) is a modifier flag on a
// row, not a rung on this ladder -- kept its own orange, distinct from E despite both being Inferred.
export const MATCH_KEY_TIER_CLASS = {
	I: 'text-violet-700',
	A: 'text-green-600',
	B: 'text-green-600',
	G: 'text-green-600',
	C: 'text-blue-700',
	D: 'text-blue-700',
	H: 'text-blue-700',
	E: 'text-red-700',
	F: 'text-orange-600',
};

export function tagMatch(item, key, transferForSenderCheck) {
	if (!item || item._matchKey) return;
	// #6144b: 'I' (O.P., Only Possibility) is the top, overriding tier -- don't let a weaker
	// signal like sender-basis ('G') displace it once a caller has determined there was no
	// other eligible candidate at all.
	if (key !== 'I' && transferForSenderCheck && senderBasisMatchesCandidate(transferForSenderCheck, item)) key = 'G';
	item._matchKey = key;
}

// Helper functions used by both dataOnly mode and renderUserGiftTransModal
export function numberToLetters(num) {
	let letters = '';
	while (num >= 0) {
		letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[num % 26] + letters;
		num = Math.floor(num / 26) - 1;
	}
	return letters;
}

export function lettersToNumbers(ltrs) {
	if (!ltrs) return '?';
	let n = 0;
	for (let p = 0; p < ltrs.length; p++) {
		n = ltrs[p].charCodeAt() - 64 + n * 26;
	}
	return n.toString().padStart(6, '0');
}

const SORT_CLASS_BASIS_DATE_QTY = true; // or client-specific flag

// By date then descending effective tax basis (unknowns last)
export function sortByClassThenBasisThenDateThenQty(transArray) {
	transArray.sort((a, b) => {
		if (SORT_CLASS_BASIS_DATE_QTY) {
			// 1) Class ascending
			if (a.shareClass < b.shareClass) return -1;
			if (a.shareClass > b.shareClass) return 1;

			// 2) Effective tax basis descending, unknowns last
			function normBasis(val) {
				return isKnownNumber(val) ? Number(val) : null;
			}

			let basisA = normBasis(a._effectiveTaxBasis);
			let basisB = normBasis(b._effectiveTaxBasis);

			if (basisA === null && basisB !== null) return 1;
			if (basisA !== null && basisB === null) return -1;

			if (basisA !== null && basisB !== null) {
				if (basisA < basisB) return 1;
				if (basisA > basisB) return -1;
			}

			// 3) Date ascending
			let dateA = Number(a.date);
			let dateB = Number(b.date);
			if (dateA < dateB) return -1;
			if (dateA > dateB) return 1;

			// 4) Quantity ascending
			let quantityA = Number(a.quantity);
			let quantityB = Number(b.quantity);
			if (quantityA < quantityB) return -1;
			if (quantityA > quantityB) return 1;

			return 0;
		}
		if (isVERM) {
			// First, sort by 'class'
			if (a.shareClass < b.shareClass) return -1;
			if (a.shareClass > b.shareClass) return 1;

			// Next, sort by 'date' ascending
			let dateA = Number(a.date);
			let dateB = Number(b.date);
			if (dateA < dateB) return -1;
			if (dateA > dateB) return 1;
		} else if (!isBB) {
			// First, sort by 'date' descending
			let dateA = Number(a.date);
			let dateB = Number(b.date);
			if (dateA < dateB) return 1;
			if (dateA > dateB) return -1;

			// Next, sort by 'class'
			if (a.shareClass < b.shareClass) return -1;
			if (a.shareClass > b.shareClass) return 1;
		}

		// Then, sort by effective tax basis (descending, unknowns last)
		function normBasis(val) {
			return isKnownNumber(val) ? Number(val) : null;
		}

		let basisA = normBasis(a._effectiveTaxBasis);
		let basisB = normBasis(b._effectiveTaxBasis);

		// Unknowns go LAST
		if (basisA === null && basisB !== null) return 1;
		if (basisA !== null && basisB === null) return -1;

		// Both known → descending
		if (basisA !== null && basisB !== null) {
			if (basisA < basisB) return 1;
			if (basisA > basisB) return -1;
		}

		if (isBB) {
			// First, sort by 'class'
			if (a.shareClass < b.shareClass) return -1;
			if (a.shareClass > b.shareClass) return 1;

			// Next, sort by 'date' ascending
			let dateA = Number(a.date);
			let dateB = Number(b.date);
			if (dateA < dateB) return -1;
			if (dateA > dateB) return 1;
		}

		// Finally, sort by 'quantity'
		let quantityA = Number(a.quantity);
		let quantityB = Number(b.quantity);
		if (quantityA < quantityB) return -1;
		if (quantityA > quantityB) return 1;

		return 0;
	});

	return transArray;
}

// Returns:
// - null    => no deferral
// - "LOCK"  => TBD / future use (admin must edit to unlock)
// - number  => unix seconds at midnight of the "use on/after" day
export function deferUseTs(notes) {
	const s = String(notes || '').trim();
	if (!s) return null;

	// Must contain the "for use" concept (or "for future use")
	if (!/\bfor\s+(?:future\s+)?use\b/i.test(s)) return null;

	// "for future use" => locked until admin edits
	if (/\bfor\s+future\s+use\b/i.test(s)) return 'LOCK';

	// Recognize TBD in any common form: TBD, T.B.D., T. B. D, etc.
	const TBD_RE = /\bt\s*\.?\s*b\s*\.?\s*d\s*\.?\b/i;

	// Allow: "... for use TBD" (no on/after keyword)
	if (/\bfor\s+use\b/i.test(s) && TBD_RE.test(s)) return 'LOCK';

	// Allow: "for use on X", "for use after X", "for use on/after X"
	const m = s.match(/\bfor\s+use\b.*?\b(on\/after|on|after)\b\s*[:\-]?\s*([A-Za-z0-9,./\- ]{2,60}|\bt\s*\.?\s*b\s*\.?\s*d\s*\.?\b)\b/i);
	if (!m) return null;

	const raw = String(m[2] || '').trim();
	if (TBD_RE.test(raw)) return 'LOCK';

	// Parse "any date format" using moment loose first, then Date.parse fallback
	let ms = null;
	if (window.moment) {
		const mm = moment(raw);
		if (mm.isValid()) ms = mm.valueOf();
	}
	if (ms == null) {
		const dp = Date.parse(raw);
		if (!isNaN(dp)) ms = dp;
	}
	if (ms == null) return null;

	return moment(ms).utc().startOf('day').unix();
}

// #6144: shared epsilon-tolerant quantity equality. Quantities are strings
// (so '.777' vs '0.777' fails a bare ==) and can carry float noise from
// upstream math -- Number() fixes the former, the epsilon fixes the latter.
export function quantitiesMatch(a, b) {
	const numA = Number(a);
	const numB = Number(b);
	if (isNaN(numA) || isNaN(numB)) return false;
	return nearlyEqualBalance(numA, numB);
}

function getLatestValuationField(txn, clientData, fieldName) {
	if (!txn?.shareClass || !txn?.date) return null;
	let vals = clientData?.equity?.[txn.shareClass]?.valuations;
	if (!Array.isArray(vals) || !vals.length) return null;
	let txnDate = Number(txn.date);
	let bestValue = null;
	let bestDate = null;
	vals.forEach((v) => {
		let vDate = Number(v?.date);
		if (!isFinite(vDate) || vDate > txnDate) return;
		if (!isKnownNumber(v?.[fieldName])) return;
		if (bestDate === null || vDate > bestDate) {
			bestDate = vDate;
			bestValue = Number(v[fieldName]);
		}
	});
	return bestValue;
}

// #6144: a sender can declare a lot's tax basis via `senderBasis` or embedded
// in notes/Description (e.g. "Gift, basis: 4.50") -- highest-priority signal in
// resolveEffectiveTaxBasis()/scoreMatches() below. Permissive on format since
// this is admin-typed free text. #6405: an explicit "basis" only -- a bare "$" amount is often a price.
function parseSenderBasisFromNotes(notesText) {
	const s = String(notesText || '');
	const m = s.match(/\bbasis\s*:?\s*\$?\s*(-?[\d,]*\.?\d+)/i);
	if (!m) return null;
	const value = Number(m[1].replace(/,/g, ''));
	return isNaN(value) ? null : value;
}

// #6144: tolerant numeric coercion for a sender-basis value that may come
// back formatted ("$4.50", "1,234.5") depending on how it was entered.
function coerceBasisNumber(raw) {
	if (raw === null || raw === undefined) return null;
	const cleaned = typeof raw === 'string' ? raw.replace(/[$,]/g, '').trim() : raw;
	if (cleaned === '') return null;
	const n = Number(cleaned);
	return isNaN(n) ? null : n;
}

export function getSenderDeclaredBasis(txn) {
	// #6405: Sender Basis is a standard field now, not a custom column found by its label
	const val = coerceBasisNumber(txn?.senderBasis);
	return val != null ? val : parseSenderBasisFromNotes(txn?.notes);
}

// True when a SEND transaction's sender-declared basis (see
// getSenderDeclaredBasis()) matches a candidate prior holding's own
// recorded taxBasis -- the high-priority matching signal used by
// scoreMatches()/scoreDifferences() below.
export function senderBasisMatchesCandidate(transfer, h) {
	const declared = getSenderDeclaredBasis(transfer);
	if (declared == null || !isKnownNumber(h?.taxBasis)) return false;
	return Math.abs(declared - Number(h.taxBasis)) < 0.005;
}

// #6144: same date-in-notes heuristic scoreDifferences() uses internally -- reused here
// only to identify (for the 'H' Key) whether a note-embedded date, not the score itself, decided a pick.
function extractNoteDate(text) {
	const match = String(text || '').match(/(\d{1,4}[\/\-]\d{1,2}[\/\-]\d{1,4}|\w{3} \d{1,2}, \d{4})/);
	if (match) {
		const m = moment(match[0], dateFormats, true);
		if (m.isValid()) return m.valueOf();
	}
	return null;
}

function isNoteDateCloseInTime(msFromNote, dateStampSeconds) {
	return Math.abs(msFromNote - Number(dateStampSeconds) * 1000) <= 5 * 24 * 60 * 60 * 1000;
}

export function noteDateMatchesCandidate(transfer, h) {
	const transferNoteDate = extractNoteDate(transfer?.notes);
	const historicNoteDate = extractNoteDate(h?.notes);
	return Boolean(
		(transferNoteDate && isNoteDateCloseInTime(transferNoteDate, h?.date)) || (historicNoteDate && isNoteDateCloseInTime(historicNoteDate, transfer?.date))
	);
}

export function resolveEffectiveTaxBasis(txn, clientData) {
	// #6144a (2026-09-12, per Chris): reordered so a real recorded number always wins
	// over any guess, and dropped the current/latest-valuation fallbacks entirely --
	// too presumptuous for a Tax Basis default. Order is now: 1) this transaction's own
	// Tax Basis (explicit n/a included -- that's a known answer, not a gap), 2) Share
	// Price actually paid (admin-entered cash overrides a Sender Basis claim), 3) Sender
	// Basis, 4) n.a.

	// 1) explicit tax basis, including explicit n/a
	let tb = String(txn.taxBasis || '')
		.trim()
		.toLowerCase();
	if (isNA(tb)) return { value: txn.taxBasis, source: 'explicitNA' };
	if (isKnownNumber(txn.taxBasis)) return { value: Number(txn.taxBasis), source: 'taxBasis' };

	// 2) purchase price / transaction price -- excludes 0, which is now the
	// display default for a never-entered Share Price (not necessarily a
	// real $0), so it's no longer distinguishable from "unknown" here.
	if (isKnownNumber(txn.sharePrice) && Number(txn.sharePrice) > 0) return { value: Number(txn.sharePrice), source: 'sharePrice' };

	// 3) sender-declared basis (dedicated field or parsed from notes)
	const senderBasis = getSenderDeclaredBasis(txn);
	if (senderBasis != null) return { value: senderBasis, source: 'senderBasis' };

	// 4) nothing known -- 'n.a.', not a guess from today's/latest valuation data.
	return { value: 'n.a.', source: 'unresolved' };
}

// #6144: per-share value to compare against cost basis for the optional Gain/Loss
// column -- prefers the latest Reference Price valuation as of today, falling back
// to the latest Share Price valuation. Internal estimate only, not tax advice.
function getCurrentPerShareValue(t, clientData) {
	const asOfToday = { shareClass: t.shareClass, date: Math.floor(Date.now() / 1000) };
	let ref = getLatestValuationField(asOfToday, clientData, 'referencePrice');
	if (isKnownNumber(ref)) return Number(ref);
	let sp = getLatestValuationField(asOfToday, clientData, 'sharePrice');
	if (isKnownNumber(sp)) return Number(sp);
	return null;
}

// #6144: estimated capital gain/loss for the optional Gain/Loss column --
// (current per-share value - effective Tax Basis) * quantity. Long-term vs.
// short-term is based on this tranche's own date, which is the acquisition
// date a gift recipient inherits under IRS basis "tacking" rules. Returns
// null when there isn't enough known data to estimate.
export function computeGainLoss(t, clientData) {
	if (!Number.isFinite(t._effectiveTaxBasis)) return null;
	const currentValue = getCurrentPerShareValue(t, clientData);
	if (!isKnownNumber(currentValue)) return null;
	const qty = Number(String(t.quantity).replace(/[^.\d-]/g, ''));
	if (!qty) return null;
	const totalGain = (currentValue - t._effectiveTaxBasis) * qty;
	const isLongTerm = moment.unix(t.date).add(1, 'year').isSameOrBefore(moment());
	return { totalGain, isLongTerm };
}

function normalizeCertToken(token) {
	let s = String(token || '')
		.trim()
		.toUpperCase();
	if (!s) return '';

	s = s.replace(/[.\-_:\/\\•|\s#]/g, '');

	let m = s.match(/^([A-Z]*)(\d+)$/);
	if (m) return m[1] + (m[2].replace(/^0+/, '') || '0');

	s = s.replace(/^0+(\d)/, '$1');
	return s;
}

// Matches cert numbers like "62-409", "SD.468" as one token (not truncated at the separator).
// Only one separator allowed, so it won't accidentally match multi-part dates.
export const CERT_TOKEN = '[A-Za-z]*[-.]?0*\\d+(?:[-.]\\d+)?';

export function parseCertificateRefs(notesText) {
	const s = String(notesText || '');
	const result = {
		newCert: '',
		sourceCerts: [],
	};

	// First cert inside parens is often the new cert
	// e.g. Transfer (#15954 from ...)
	let m = s.match(new RegExp('\\((?:cert\\s*)?#?\\s*(' + CERT_TOKEN + ')', 'i'));
	if (m && m[1]) result.newCert = normalizeCertToken(m[1]);

	// Anything after "from" is a source list
	// e.g. from 14479, 14506, 14507
	m = s.match(/\bfrom\b(.*?)(?:\)|$)/i);
	if (m && m[1]) {
		const sourceCertPattern = new RegExp('\\b(' + CERT_TOKEN.replace('\\d+', '\\d{4,}') + ')\\b');
		result.sourceCerts = m[1]
			.split(',')
			.map((part) => {
				const mm = part.match(sourceCertPattern);
				return mm && mm[1] ? normalizeCertToken(mm[1]) : '';
			})
			.filter(Boolean);
	}

	// Fallback for simple cases like Cancellation (0006620)
	if (!result.newCert && !result.sourceCerts.length) {
		m = s.match(new RegExp('\\((?:cert\\s*)?#?\\s*(' + CERT_TOKEN + ')\\)', 'i'));
		if (m && m[1]) result.newCert = normalizeCertToken(m[1]);
	}

	// Bare notation, no parens at all, e.g. "Issuance 2094" -- old pre-electronic
	// entries. Anchored to the whole note (verb + cert, nothing else) so it can't
	// misfire on richer free text.
	if (!result.newCert && !result.sourceCerts.length) {
		m = s.match(new RegExp('^[A-Za-z]+(?:\\s+[A-Za-z]+)*\\s+(' + CERT_TOKEN + ')$'));
		if (m && m[1]) result.newCert = normalizeCertToken(m[1]);
	}

	// Cancellations that list multiple certs together with no "from", e.g.
	// "Stock Certificate Cancellation (62-422, 62-1051)" -- only the first was
	// being captured above. Scoped to "cancel" notes so nothing else changes.
	if (!result.sourceCerts.length && /cancel/i.test(s)) {
		const parenMatch = s.match(/\(([^)]+)\)/);
		if (parenMatch) {
			const tokens = (parenMatch[1].match(new RegExp(CERT_TOKEN, 'g')) || []).map(normalizeCertToken).filter(Boolean);
			if (tokens.length > 1) {
				result.sourceCerts = [...new Set(tokens)];
				result.newCert = '';
			}
		}
	}

	return result;
}

function toNumber(str) {
	let num = Number(str);
	return isNaN(num) ? 1 : num; // assumed share price, zero is trouble mathwise!
}

/**
 * #6602: the matching ladder, moved verbatim out of giftTransfer(). Reconstructs a holder's
 * remaining tranches (giftTransactions), consumed sources (historicHoldings) and outgoing
 * transfers (priorTransfers, filled in place). Inputs are giftTransfer()'s prepared arrays.
 */
export function runMatchingLadder({ classes, giftTransactions, historicHoldings, priorTransfers, giftUserId, clientData, certColumn }) {
	const CERT_COLUMN = certColumn;
	let pairedQty = 0;
	let minDiff = 999; // arbitrarily large starting number used in tranche analysis
	let diffCount = 0; // counter used in tranche analysis

	// On first pass, go class-by-class.  May need to revisit with mixed classes for swaps.
	classes.forEach((c) => {
		// First, find dates with multiple transations on the same date
		let transactionsDateCount = giftTransactions
			.filter((g) => g.shareClass == c)
			.reduce((t, r) => {
				t[r.date] = (t[r.date] || 0) + 1;
				return t;
			}, {});
		let multipleSameDates = Object.keys(transactionsDateCount).filter((date) => transactionsDateCount[date] >= 1);
		multipleSameDates.push(Math.floor(Date.now() / 1000).toString()); // add today to catch any recent single sends.
		multipleSameDates.sort((a, b) => Number(a) - Number(b));
		console.log('multipleSameDates[' + c + ']: ', multipleSameDates);

		const usedSendNrids = new Set();
		function addPriorTransfers(transArray) {
			transArray.forEach(addPriorTransfer);
		}
		function addPriorTransfer(trans) {
			if (!trans?.nrid || usedSendNrids.has(trans.nrid)) return false;
			priorTransfers.push(trans);
			usedSendNrids.add(trans.nrid);
			return true;
		}

		let carryOverDeduct = 0;

		// Now, examine each of these dates, starting with the oldest one
		multipleSameDates.forEach(function (d) {
			console.log('### SAME DATE ###: ', moment.unix(d).utc().format('DD-MMM-YY'));

			// First handle any earlier single Send that occurs on a given date by itself
			let singleSendTransactions = giftTransactions.filter((g) => sameClassAndSender(g) && earlierDate(g));
			singleSendTransactions.forEach((singleSend) => {
				let targetQty = Number(singleSend.quantity);
				if (handleNotesMatch(singleSend, targetQty, Number(singleSend.date))) {
					console.log('NOTES MATCH Transaction: ', singleSend);
					singleSend.tranche = c + '-' + singleSend.date;
					addPriorTransfer(singleSend);
					giftTransactions = giftTransactions.filter((g) => g.nrid != singleSend.nrid);
				} else if (handlePerfectFit(singleSend, targetQty, Number(singleSend.date) - 43200, false)) {
					// 1/2 day
					console.log('SINGLE SEND Transaction: ', singleSend);
					singleSend.tranche = c + '-' + singleSend.date;
					addPriorTransfer(singleSend);
					giftTransactions = giftTransactions.filter((g) => g.nrid != singleSend.nrid);
				} else if (handlePerfectFit(singleSend, targetQty, Number(singleSend.date), false)) {
					console.log('SINGLE SEND Transaction: ', singleSend);
					singleSend.tranche = c + '-' + singleSend.date;
					addPriorTransfer(singleSend);
					giftTransactions = giftTransactions.filter((g) => g.nrid != singleSend.nrid);
				} else {
					console.log('*** NO SEND-ONLY MATCH ***');
					let safety = 0;
					let firstHistoric;
					// #6191: prefer a candidate whose own recorded tax basis matches what the sender
					// declared for this send (getSenderDeclaredBasis()/senderBasisMatchesCandidate()) --
					// otherwise this loop picks purely by array position (reversed), which can silently
					// draw shares from the wrong basis lot when an unrelated same-day receive is also in
					// play. Falls back to the original reversed-array search when no basis is declared or
					// no candidate matches it.
					const declaredBasis = getSenderDeclaredBasis(singleSend);
					while (targetQty > 0 && safety < 20) {
						safety++;
						// #6144h: exclude active ("#") certificates from both candidate searches below --
						// same protection oneForOneMatches/handlePerfectFit already give their pools.
						let firstPrior =
							(declaredBasis != null &&
								giftTransactions.find(
									(g) =>
										!activeCertificate(g) &&
										sameClassAndReceiver(g) &&
										Number(g.date) <= Number(singleSend.date) &&
										eligibleAsOf(g, singleSend.date) &&
										isKnownNumber(g.taxBasis) &&
										Math.abs(Number(g.taxBasis) - declaredBasis) < 0.005
								)) ||
							giftTransactions
								.slice()
								.reverse()
								.find(
									(g) =>
										!activeCertificate(g) &&
										sameClassAndReceiver(g) &&
										Number(g.date) <= Number(singleSend.date) &&
										eligibleAsOf(g, singleSend.date)
								);
						if (firstPrior) {
							if (firstPrior.quantity > targetQty) {
								firstPrior.quantity = (Number(firstPrior.quantity) - targetQty).toString();
								firstHistoric = JSON.parse(JSON.stringify(firstPrior));
								firstHistoric.quantity = targetQty.toString();
								firstHistoric.tranche = c + '-' + singleSend.date;
								historicHoldings.push(firstHistoric);
								tagMatch(firstHistoric, 'E', singleSend);
								targetQty = 0;
							} else {
								targetQty -= Number(firstPrior.quantity);
								firstPrior.tranche = c + '-' + singleSend.date;
								historicHoldings.push(firstPrior);
								tagMatch(firstPrior, 'E', singleSend);
								giftTransactions = giftTransactions.filter((g) => g.nrid != firstPrior.nrid);
							}
						}
					}
					console.log('SINGLE SEND Transaction: ', singleSend);
					singleSend.tranche = c + '-' + singleSend.date;
					addPriorTransfer(singleSend);
					giftTransactions = giftTransactions.filter((g) => g.nrid != singleSend.nrid);
				}
			});

			let sameDateTransactions = giftTransactions.filter((t) => t.date == d && t.shareClass == c);
			let sameDateSends = sameDateTransactions.filter((t) => t.sender == giftUserId);
			let sameDateReceives = sameDateTransactions.filter((t) => t.receiver == giftUserId);

			const certPairs = CERT_COLUMN ? matchSameDayByCertificate(sameDateTransactions) : [];
			if (certPairs.length) {
				certPairs.forEach(({ send, receive }) => {
					send.tranche = c + '-' + d;
					receive.tranche = c + '-' + d;
					addPriorTransfer(send);
					const certPairHolding = { ...receive, tranche: c + '-' + d, quantity: send.quantity };
					tagMatch(certPairHolding, 'A');
					historicHoldings.push(certPairHolding);

					giftTransactions = giftTransactions.filter((g) => g.nrid != send.nrid && g.nrid != receive.nrid);
				});

				sameDateTransactions = giftTransactions.filter((t) => t.date == d && t.shareClass == c);
				sameDateSends = sameDateTransactions.filter((t) => t.sender == giftUserId);
				sameDateReceives = sameDateTransactions.filter((t) => t.receiver == giftUserId);
			}

			// #6108c (Vermeer): a same-day Receive re-Transferred out same-day under a DIFFERENT
			// cert/reference number (e.g. Reissuance (2618)=214 -> Transfer (2619)=214) isn't caught
			// by matchSameDayByCertificate() above (same cert number required on both sides). Left
			// alone, parseTransfers() has to explain this Receive from priorGrossQty -- the sum of
			// ALL same-date sends, including unrelated Cancellations that have their own correct
			// historic matches elsewhere -- and its blind subset-sum can consume completely unrelated
			// historic lots to hit that inflated target. Net the pass-through by exact quantity here,
			// before any other same-day matcher runs.
			const quantityPairs = CERT_COLUMN ? matchSameDayByQuantityPassthrough(sameDateTransactions) : [];
			if (quantityPairs.length) {
				quantityPairs.forEach(({ send, receive }) => {
					send.tranche = c + '-' + d;
					receive.tranche = c + '-' + d;
					addPriorTransfer(send);
					const passthroughHolding = { ...receive, tranche: c + '-' + d, quantity: send.quantity };
					tagMatch(passthroughHolding, 'C');
					historicHoldings.push(passthroughHolding);

					giftTransactions = giftTransactions.filter((g) => g.nrid != send.nrid && g.nrid != receive.nrid);
				});

				sameDateTransactions = giftTransactions.filter((t) => t.date == d && t.shareClass == c);
				sameDateSends = sameDateTransactions.filter((t) => t.sender == giftUserId);
				sameDateReceives = sameDateTransactions.filter((t) => t.receiver == giftUserId);
			}

			// Next process any notes-match transfers (remove as we go)
			const descMatchedSendNrids = new Set();
			// #6144w: a multi-send same-day batch (2+ sends) can have EVERY send independently
			// name its OWN bare cert (no "from" clause, e.g. "Redemption (2094)"), each uniquely
			// matching a distinct earlier lot of the identical quantity -- resolve those FIRST,
			// before the batch-level blind quantity-sum matching below (exactMatchTrans /
			// findSingleCertExactMatch) gets a chance to grab an unrelated same-quantity cert
			// instead. Real incident: Reed Kirk Asplundh's "Redemption (2094)" + "Redemption
			// (1727)" (100+100) wrongly summed to and consumed "Issuance (1617)", a genuinely
			// unrelated 200-share cert. All-or-nothing: only commits when EVERY send in the batch
			// resolves this way, never partially -- a batch this can't fully explain is left
			// untouched for the existing matchers, so this never perturbs an account (e.g. Edward
			// K. Asplundh) whose same-day batches need the existing blind-sum/subset logic.
			if (sameDateSends.length > 1) {
				const candidatePairs = [];
				const claimedNrids = new Set();
				let allResolved = true;
				for (const send of sameDateSends) {
					const ownToken = getLotCertToken(send.notes);
					if (!ownToken) {
						allResolved = false;
						break;
					}
					const tokenMatches = giftTransactions.filter(
						(g) =>
							sameClassAndReceiver(g) &&
							!activeCertificate(g) &&
							!claimedNrids.has(g.nrid) &&
							earlierDate(g) &&
							eligibleAsOf(g, send.date) &&
							getLotCertToken(g.notes) === ownToken
					);
					if (tokenMatches.length !== 1 || Number(tokenMatches[0].quantity) !== Number(send.quantity)) {
						allResolved = false;
						break;
					}
					claimedNrids.add(tokenMatches[0].nrid);
					candidatePairs.push({ send, match: tokenMatches[0] });
				}
				if (allResolved && candidatePairs.length) {
					candidatePairs.forEach(({ send, match }) => {
						const trancheItem = { ...match, tranche: c + '-' + send.date };
						tagMatch(trancheItem, 'A');
						historicHoldings.push(trancheItem);
						send.tranche = c + '-' + send.date;
						addPriorTransfer(send);
						descMatchedSendNrids.add(send.nrid);
					});
					giftTransactions = giftTransactions.filter((g) => !claimedNrids.has(g.nrid) && !descMatchedSendNrids.has(g.nrid));
					sameDateTransactions = giftTransactions.filter((t) => t.date == d && t.shareClass == c);
					sameDateSends = sameDateTransactions.filter((t) => t.sender == giftUserId);
					sameDateReceives = sameDateTransactions.filter((t) => t.receiver == giftUserId);
				}
			}
			// #6144n: don't let a Send drain a cert some sibling same-day Receive explicitly
			// names for itself (e.g. from a copy/paste source list that just happens to include
			// it) -- parseTransfers()'s handlePerfectFit (#6144l, and its partial-lot draw,
			// #6144o) resolves that Receive's own claim instead. A cert no Receive claims at
			// all is unaffected -- this is the only place that will ever find it.
			const contestedCerts = CERT_COLUMN ? new Set(sameDateReceives.flatMap((r) => Array.from(getExplicitSourceCertSet(r.notes) || []))) : null;
			// IMPORTANT: iterate a snapshot because we mutate giftTransactions inside the loop
			for (const send of [...sameDateSends]) {
				if (handleNotesMatch(send, Number(send.quantity), Number(send.date), contestedCerts)) {
					console.log('NOTES MATCH Transaction: ', send);
					send.tranche = c + '-' + send.date;
					addPriorTransfer(send);
					// Remove the SEND from the active pool so later branches don't touch it
					descMatchedSendNrids.add(send.nrid);
				}
			}
			// Apply removals once (avoids mid-loop topology weirdness)
			if (descMatchedSendNrids.size) giftTransactions = giftTransactions.filter((t) => !descMatchedSendNrids.has(t.nrid));
			// Recompute same-day lists after removing desc-matched sends
			sameDateTransactions = giftTransactions.filter((t) => t.date == d && t.shareClass == c);
			sameDateSends = sameDateTransactions.filter((t) => t.sender == giftUserId);
			sameDateReceives = sameDateTransactions.filter((t) => t.receiver == giftUserId);

			// Next look for same day cancel/reissue (e.g. lost certificate)
			if (isEDM && sameDateSends.length && sameDateReceives.length >= 1) releaseRestrictedEquity(sameDateTransactions);

			const PREFER_CERT_LINEAGE = CERT_COLUMN && !isASP;
			const hasSelfContainedCertFlow = isSelfContainedCertificateFlow(sameDateTransactions);
			const shouldApplyCertLineage = PREFER_CERT_LINEAGE && !hasSelfContainedCertFlow && sameDateSends.length;
			if (shouldApplyCertLineage) cancelReissueCertificate(sameDateTransactions);

			// Next, more complex scenarios
			sameDateTransactions = giftTransactions.filter((t) => t.date == d && t.shareClass == c);
			sameDateTransactions.sort((a, b) => Number(b.quantity) - Number(a.quantity));
			sameDateSends = sameDateTransactions.filter((t) => t.sender == giftUserId);
			sameDateReceives = sameDateTransactions.filter((t) => t.receiver == giftUserId);
			let sameDateSenders = [...new Set(sameDateTransactions.map((t) => t.sender))];
			let sameDateReceivers = [...new Set(sameDateTransactions.map((t) => t.receiver))];
			let sameDateSendQtySum = sameDateSends.reduce((a, b) => a + Number(b.quantity), 0);
			let sameDateReceiveQtySum = sameDateReceives.reduce((a, b) => a + Number(b.quantity), 0);
			let sameDateSendAmtSum = sameDateSends.reduce((a, b) => a + Number(b.quantity) * toNumber(b.sharePrice), 0);
			let sameDateReceiveAmtSum = sameDateReceives.reduce((a, b) => a + Number(b.quantity) * toNumber(b.sharePrice), 0);
			let sameDateNetQty = Math.abs(sameDateSendQtySum - sameDateReceiveQtySum);
			let sameDateNetAmt = Math.abs(sameDateSendAmtSum - sameDateReceiveAmtSum);
			// #6144t: every txn in a $0-priced account nets sameDateNetAmt to a trivial 0,
			// so the amt-based SPLIT branch below is meaningless noise there -- gate it on
			// actual non-zero price data existing for this date's batch.
			let sameDateHasPriceAmt = sameDateSendAmtSum > 0 || sameDateReceiveAmtSum > 0;
			// console.log("sameDate: ", d);
			// console.log("sameDateTrans: ", sameDateTransactions);
			// console.log("Senders: ", sameDateSenders);
			// console.log("Receivers: ", sameDateReceivers);
			// console.log("Sends: ", sameDateSends);
			// console.log("Receives: ", sameDateReceives);
			// console.log("SendQtySum: ", sameDateSendQtySum);
			// console.log("ReceiveQtySum: ", sameDateReceiveQtySum);
			// console.log("SendAmtSum: ", sameDateSendAmtSum);
			// console.log("ReceiveAmtSum: ", sameDateReceiveAmtSum);

			let isSwapOrSplit = false;
			if (sameDateSends.length == 0) {
				// ONE WAY recieve transactions - do nothing
				console.log('ONE-WAY Receiving: ', sameDateReceives);
			} else if (sameDateReceives.length == 0) {
				// ONE WAY send transactions - look for matches
				console.log('ONE-WAY Sending: ', sameDateSends);

				if (moment.unix(Number(d)).utc().format('YYYY-MM-DD') == '2015-04-08' && c == 'C') {
					console.log(
						'AL sameDateSends:',
						sameDateSends.map((s) => ({
							nrid: s.nrid,
							notes: s.notes,
							quantity: s.quantity,
						}))
					);
				}

				// First try finding old certificates, where each exactly matches one of the Sends
				let exactMatches = oneForOneMatches(sameDateSends);
				if (exactMatches) return;

				// Attempt 1 - first look for one prior Receive that exactly matches the collection of Sends
				// #6144u: exclude active ("#") certs -- handlePerfectFit/oneForOneMatches already do, this search didn't.
				let exactMatchTrans = giftTransactions.find(
					(g) =>
						sameClassAndReceiver(g) &&
						!activeCertificate(g) &&
						earlierDate(g) &&
						eligibleAsOf(g, d) &&
						sameQty(g, sameDateSendQtySum) &&
						!(sameDateSends.length === 1 && cancellationCertMismatch(sameDateSends[0].notes, g.notes))
				);
				if (exactMatchTrans) {
					let sendTransactions = giftTransactions.filter((g) => sameClassAndSender(g) && sameDate(g));
					sendTransactions.forEach((s) => (s.tranche = c + '-' + d));
					addPriorTransfers(sendTransactions);
					console.log('SEND Transactions: ', sendTransactions);
					// FIX: only remove the sends we *actually captured*, not everything <= date
					const sendNridSet = new Set(sendTransactions.map((t) => t.nrid).filter(Boolean));
					giftTransactions = giftTransactions.filter((g) => !sendNridSet.has(g.nrid));
					// giftTransactions = giftTransactions.filter(g => !sameClassAndSender(g) || !sameDate(g));
					giftTransactions = giftTransactions.filter((g) => g.nrid != exactMatchTrans.nrid);
					exactMatchTrans.tranche = c + '-' + d;
					tagMatch(exactMatchTrans, 'C', sameDateSends[0]);
					historicHoldings.push(exactMatchTrans);
				} else {
					// Need to tear things apart, Send by Send
					let closeMatchTrans;
					sameDateSends.forEach((s) => {
						// Attempt 2 & 3 -Try to find one or more prior Receives that match this Send
						exactMatchTrans = giftTransactions.find(
							(g) =>
								sameClassAndReceiver(g) &&
								earlierDate(g) &&
								eligibleAsOf(g, s.date) &&
								sameQty(g, Number(s.quantity)) &&
								!cancellationCertMismatch(s.notes, g.notes) &&
								(notRestricted(g) || !isEDM)
						);
						if (exactMatchTrans || !parseTransfers()) {
							let sendTransaction = giftTransactions.filter((g) => g.nrid == s.nrid)[0];
							if (!sendTransaction) return;
							sendTransaction.tranche = c + '-' + d;
							addPriorTransfer(sendTransaction);
							giftTransactions = giftTransactions.filter((g) => g.nrid != s.nrid);
							if (exactMatchTrans) {
								// <- Good, a simple solution, all done (Attempt 2)
								giftTransactions = giftTransactions.filter((g) => g.nrid != exactMatchTrans.nrid);
								exactMatchTrans.tranche = c + '-' + d;
								tagMatch(exactMatchTrans, 'C', sendTransaction);
								historicHoldings.push(exactMatchTrans);
							} else if (!closeMatchTrans) {
								// <- Need to score "open" prior Receives to find the best one (Attempt 3 & 4)
								// #6144h: exclude active ("#") certificates -- oneForOneMatches/handlePerfectFit
								// already do; this pool was the odd one out.
								let openTransactions = giftTransactions.filter(
									(g) =>
										!activeCertificate(g) &&
										sameClassAndReceiver(g) &&
										earlierDate(g) &&
										!g.tranche &&
										eligibleAsOf(g, d) && // or sendTransaction.date ??
										(notRestricted(g) || !isEDM)
								);
								if (openTransactions.length) {
									// After finding the closest match, clone this Receive as a "Twin" and move on to the next Send for this date
									closeMatchTrans = scoreMatches(sendTransaction, openTransactions);
									console.log('CLOSE Match: ', closeMatchTrans);
									if (closeMatchTrans) {
										// need new object
										closeMatchTrans = JSON.parse(JSON.stringify(closeMatchTrans));
										// giftTransactions = giftTransactions.filter(g => g.nrid != bestMatchTrans.nrid);
										closeMatchTrans.tranche = c + '-' + d;
										if (!closeMatchTrans.before) closeMatchTrans.before = closeMatchTrans.quantity;
										closeMatchTrans.quantity = s.quantity;
										// #6144b: only one open candidate -> 'I' (O.P.), not 'E' -- no guessing was involved.
										tagMatch(closeMatchTrans, openTransactions.length == 1 ? 'I' : 'E', sendTransaction);
										historicHoldings.push(closeMatchTrans);
									}
								}
							}
						}
						// For a close match (not exact), deduct the from the "Twin" the quantity of every Send
						if (closeMatchTrans) {
							let matchTwin = giftTransactions.find((h) => h.nrid == closeMatchTrans.nrid);
							if (!matchTwin.before) matchTwin.before = Number(matchTwin.quantity);
							matchTwin.quantity = Number(matchTwin.quantity) - Number(s.quantity);
							if (carryOverDeduct) {
								matchTwin.quantity += carryOverDeduct;
								carryOverDeduct = 0;
							}
							if (matchTwin.quantity < 0) {
								carryOverDeduct = matchTwin.quantity;
								matchTwin.quantity = 0;
							}
							matchTwin.quantity = matchTwin.quantity.toString();
						}
						closeMatchTrans = null; // reset
					});
				}
			} else if (sameDateReceivers.length < 3 && (sameDateNetQty < 0.1 || (sameDateHasPriceAmt && sameDateNetAmt < 0.1))) {
				if (sameDateNetQty < 0.1) {
					if (sameDateSenders.sort().toString() != sameDateReceivers.sort().toString()) {
						// Attempt 1 - it's a TRANSFER
						let uniqueSenders = sameDateSenders.filter((u) => !sameDateReceivers.includes(u));
						let uniqueReceivers = sameDateReceivers.filter((u) => !sameDateSenders.includes(u));
						console.log('TRANSFER Transactions: ', uniqueSenders.concat(uniqueReceivers));
					}
					// Attempt 1 - it's a SWAP
					isSwapOrSplit = true;
					parseTransfers();
				} else if (sameDateNetAmt < 0.1) {
					// Attempt 2 - it's a SPLIT
					isSwapOrSplit = true;
					parseTransfers();
				}
			} else {
				// Attempt 2 - it might be a Vermeer-style reissue transfer
				// Look for a pair transaction with the issuer (equal quantity, cancellation/re-issue)
				pairedQty = 0;
				let exactMatch = false;
				// Remove active certificates - NO!!
				// sameDateSends = sameDateSends.filter(s => !activeCertificate(s));
				sameDateSends.forEach((send) => {
					sameDateReceives.forEach((receive) => {
						if (quantitiesMatch(send.quantity, receive.quantity)) pairedQty += Number(receive.quantity);
					});
				});

				// First try finding old certificates, where each quantity exactly matches one of the Sends
				if (pairedQty && !exactMatch) exactMatch = oneForOneMatches(sameDateSends);

				// Next try to find one old certificate that is an exact match for the collection of Sends
				// #6144u: exclude active ("#") certs here too -- same gap as the ONE-WAY Sending branch above.
				let exactMatchTrans = giftTransactions.find(
					(g) =>
						sameClassAndReceiver(g) &&
						!activeCertificate(g) &&
						earlierDate(g) &&
						sameQty(g, sameDateSendQtySum) &&
						!(sameDateSends.length === 1 && cancellationCertMismatch(sameDateSends[0].notes, g.notes))
				);
				if (exactMatchTrans) {
					giftTransactions = giftTransactions.filter((g) => g.nrid != exactMatchTrans.nrid);
					exactMatchTrans.tranche = c + '-' + d;
					tagMatch(exactMatchTrans, 'C', sameDateSends[0]);
					historicHoldings.push(exactMatchTrans);
					// Remove all of the associated send transactions
					let sendTransactions = giftTransactions.filter((g) => sameClassAndSender(g) && thisDateOrEarlier(g));
					sendTransactions.forEach((s) => (s.tranche = c + '-' + d));
					sendTransactions.forEach((adjTrans) => {
						carryForwardBasisFields(adjTrans, exactMatchTrans);
					});
					addPriorTransfers(sendTransactions);
					console.log('SEND Transactions: ', sendTransactions);
					// FIX: only remove the sends we *actually captured*, not everything <= date
					const sendNridSet = new Set(sendTransactions.map((t) => t.nrid).filter(Boolean));
					giftTransactions = giftTransactions.filter((g) => !sendNridSet.has(g.nrid));
					// giftTransactions = giftTransactions.filter(g => !sameClassAndSender(g) || !thisDateOrEarlier(g));
					sameDateReceives.forEach((adjTrans) => {
						carryForwardBasisFields(adjTrans, exactMatchTrans);
					});
					exactMatch = true;
				}

				let handledByParseTransfers = false;

				// If that fails, start rolling up old certificates using stub logic below
				if (pairedQty && !exactMatch) handledByParseTransfers = parseTransfers();

				// It's not a Vermeer-style reissue...
				if (!pairedQty && sameDateReceives.length == 1 && sameDateSends.length > 1) {
					// If net sending (no STUB), bail out.  Not sure how to handle if mult-legged....
					if (sameDateReceiveAmtSum > sameDateSendAmtSum + 1) {
						console.log('NET Receive Amount: ', sameDateReceiveAmtSum - sameDateSendAmtSum);
					}
					// It's a STUB
					console.log('NET Send Amount: ', sameDateSendAmtSum - sameDateReceiveAmtSum);
					handledByParseTransfers = parseTransfers();
				}

				// Finally look for cancel/reissue certificate number
				// #6144w: don't re-run this when exactMatchTrans above already fully resolved
				// the batch -- real incident: Edward K. Asplundh's single send "Redemption (1939)"
				// got consumed once by exactMatchTrans (a blind quantity match) and then AGAIN here
				// by canceledCertificate (a cert-token match), pulling in two historic holdings
				// (Issuance (404) and Issuance (1939)) for the one send, doubling the tranche.
				if (CERT_COLUMN && !handledByParseTransfers && !exactMatch) canceledCertificate(sameDateSends);
			}

			function matchSameDayByCertificate(sameDateTransactions) {
				const sends = sameDateTransactions.filter((t) => t.sender == giftUserId);
				const receives = sameDateTransactions.filter((t) => t.receiver == giftUserId);

				const usedReceiveNrids = new Set();
				const exactPairs = [];

				sends.forEach((send) => {
					const sendRefs = parseCertificateRefs(send.notes);
					const sendCert = sendRefs.newCert || (sendRefs.sourceCerts?.length == 1 ? sendRefs.sourceCerts[0] : '');
					if (!sendCert) return;

					const match = receives.find((r) => {
						if (usedReceiveNrids.has(r.nrid)) return false;

						const receiveRefs = parseCertificateRefs(r.notes);
						const receiveCert = receiveRefs.newCert || (receiveRefs.sourceCerts?.length == 1 ? receiveRefs.sourceCerts[0] : '');
						if (!receiveCert) return false;

						return receiveCert == sendCert && quantitiesMatch(r.quantity, send.quantity);
					});

					if (match) {
						usedReceiveNrids.add(match.nrid);
						exactPairs.push({ send, receive: match });
					}
				});

				return exactPairs;
			}

			// #6108c (Vermeer): pairs a same-day Send (an actual Transfer, never a Cancellation TO
			// the issuer -- that must still be matched against its own historic lot below) against a
			// same-day issuer-sourced Receive (a freshly manufactured cert, not an independent
			// third-party transfer-in with its own history) of the identical quantity. Excludes any
			// Receive tagged active/"#" (activeCertificate()) -- that convention marks a cert that
			// must persist as a live, current tranche, never get archived as a consumed historic
			// source just because its quantity coincides with an unrelated same-day Send. Exactly
			// one candidate must exist on each side -- an ambiguous multi-way quantity tie is left
			// untouched rather than guessed at.
			function matchSameDayByQuantityPassthrough(sameDateTransactions) {
				const sends = sameDateTransactions.filter((t) => t.sender == giftUserId && !toIssuer(t));
				const receives = sameDateTransactions.filter((t) => t.receiver == giftUserId && fromIssuer(t) && !activeCertificate(t));
				const pairs = [];
				const usedReceiveNrids = new Set();
				sends.forEach((send) => {
					const sendTwins = sends.filter((s) => quantitiesMatch(s.quantity, send.quantity));
					if (sendTwins.length !== 1) return; // ambiguous on the send side -- leave to existing matchers
					const receiveMatches = receives.filter((r) => !usedReceiveNrids.has(r.nrid) && quantitiesMatch(r.quantity, send.quantity));
					if (receiveMatches.length !== 1) return; // no match, or ambiguous
					usedReceiveNrids.add(receiveMatches[0].nrid);
					pairs.push({ send, receive: receiveMatches[0] });
				});
				return pairs;
			}

			function isSelfContainedCertificateFlow(transactions) {
				const getSourceCert = (notes) => {
					const match = notes?.match(/from (\d+)/);
					if (match) return match[1];
					const fallback = notes?.match(/\(#?(\d+)/);
					return fallback ? fallback[1] : null;
				};
				let sourceCerts = new Set();
				transactions.forEach((t) => {
					if (t.sender !== 'asplundh') {
						const cert = getSourceCert(t.notes);
						if (cert) sourceCerts.add(cert);
					}
				});
				return sourceCerts.size === 1;
			}

			function releaseRestrictedEquity(sameDaySends) {
				console.log('Begining sameDateSends: ', sameDaySends);
				// First try finding certificates, where each exactly matches one of the Sends
				let nonMatch;
				let matchCount = 0;
				sameDaySends.forEach((send) => {
					if (!toIssuer(send)) return;
					console.log('This Send: ', send);
					let exactMatches = giftTransactions.filter(
						(g) =>
							send.nrid != g.nrid &&
							quantitiesMatch(send.quantity, g.quantity) &&
							sameClassAndReceiver(g) &&
							sameDate(g) &&
							sameCertificate(g.notes, send.notes)
					);
					let exactMatch = !exactMatches ? null : exactMatches.length == 1 ? exactMatches[0] : scoreMatches(send, exactMatches);
					console.log('Very close match: ', exactMatch);
					if (exactMatch) {
						let sendQty = Number(send.quantity);
						let otherSends = giftTransactions.filter((g) => send.nrid != g.nrid && sameClassAndSender(g) && sameDate(g) && !isReleaseLike(g));
						console.log('Other sends: ', otherSends);
						let otherSend = otherSends.length == 1 ? otherSends[0] : scoreDifferences(send, otherSends);
						let otherSendQty = otherSend ? Number(otherSend.quantity) : 0;
						// if (otherSends.length) otherSendQty = otherSends.reduce((sum, g) => sum + Number(g.quantity), 0);

						// Get the other receive tranche, the non-match
						let nonMatches = giftTransactions.filter(
							(g) =>
								g.nrid !== exactMatch.nrid &&
								sameClassAndReceiver(g) &&
								thisDateOrEarlier(g) &&
								eligibleAsOf(g, send.date) &&
								Number(g.quantity) >= sendQty
						);
						if (nonMatches?.length) {
							// handle the nonMatch as new object
							nonMatch = JSON.parse(JSON.stringify(nonMatches.length == 1 ? nonMatches[0] : scoreDifferences(send, nonMatches)));
							let nonMatchQty = Number(nonMatch.quantity);
							// If excess quantity, split the NON MATCH. otherwise remove the NON MATCH from current gift transactions
							if (nonMatchQty < sendQty || otherSendQty > sendQty) return false;
							else {
								let origNonMatch = giftTransactions.find((g) => g.nrid == nonMatch.nrid);
								origNonMatch.quantity = (nonMatchQty - sendQty).toString();
								send.quantity = (sendQty - otherSendQty).toString();
								exactMatch.quantity = send.quantity;
							}

							nonMatch.quantity = sendQty.toString();
							nonMatch.tranche = c + '-' + d;
							// #6144b: only one eligible candidate -> 'I' (O.P.), not 'C' -- no guessing was involved.
							tagMatch(nonMatch, nonMatches.length == 1 ? 'I' : 'E', send);
							// 'G' (sender-declared basis) is the stronger signal -- only promote to 'H' if G didn't already win.
							if (nonMatch._matchKey !== 'G' && noteDateMatchesCandidate(send, nonMatch)) nonMatch._matchKey = 'H';
							historicHoldings.push(nonMatch);

							matchCount++;
							processSendTransaction(send);
							// if (otherSends.length) otherSends.forEach(processSendTransaction);
							if (otherSend) processSendTransaction(otherSend);
						}
					}
				});
				console.log('Ending sameDateSends: ', sameDaySends);
				return matchCount > 0;

				function processSendTransaction(g) {
					g.tranche = c + '-' + d;
					giftTransactions = giftTransactions.filter((x) => x.nrid !== g.nrid);
					carryForwardBasisFields(g, nonMatch);
					addPriorTransfer(g);
					console.log('SEND Transaction: ', g);
					sameDateSends = sameDateSends.filter((s) => s.nrid !== g.nrid);
				}
			}

			function cancelReissueCertificate(sameDateSends) {
				console.log('sameDateSends: ', sameDateSends);
				// #6108b (Vermeer): identity match, not sameCertificate()'s lineage match --
				// a same-day Reissuance(Y from X) is X's successor, not what X's Cancellation consumed.
				function sameCertificateIdentity(transNotes, notes) {
					if (!CERT_COLUMN) return areSimilar(transNotes, notes);
					const a = parseCertificateRefs(transNotes);
					const b = parseCertificateRefs(notes);
					return !!(a.newCert && b.newCert && a.newCert == b.newCert);
				}
				// First try finding certificates, where each exactly matches one of the Sends
				let matchCount = 0;
				sameDateSends.forEach((send) => {
					if (!toIssuer(send)) return;
					console.log('This Send: ', send);
					let exactMatches = giftTransactions.filter(
						(g) =>
							send.nrid != g.nrid &&
							quantitiesMatch(send.quantity, g.quantity) &&
							sameClassAndReceiver(g) &&
							sameDate(g) &&
							sameCertificateIdentity(g.notes, send.notes)
					);
					let exactMatch = !exactMatches ? null : exactMatches.length == 1 ? exactMatches[0] : scoreMatches(send, exactMatches);
					console.log('SAME Certificate: ', exactMatch);
					if (exactMatch) {
						// #6144f: exactMatch is the same-day REISSUANCE side of a cancel+reissue pair.
						// If the cancelled cert ALSO still has its own, separately-dated holding open
						// (e.g. a partial Buy-Back already drew it down without closing it out), that
						// older holding is what this Cancellation is really supposed to close -- not
						// the brand-new reissuance, which is a live holding in its own right and has
						// nothing further disposing of it. Defer to the general matchers below
						// (oneForOneMatches/parseTransfers) instead of archiving the reissuance.
						const olderSameCertHolding = giftTransactions.some(
							(g) => g.nrid != exactMatch.nrid && sameClassAndReceiver(g) && !sameDate(g) && sameCertificate(g.notes, send.notes)
						);
						if (olderSameCertHolding) return;

						matchCount++;
						send.tranche = c + '-' + d;
						// Remove the SEND from current gift transactions
						giftTransactions = giftTransactions.filter((g) => g.nrid != send.nrid);
						let matches = giftTransactions.filter(
							(g) =>
								send.nrid != g.nrid &&
								quantitiesMatch(send.quantity, g.quantity) &&
								sameClassAndReceiver(g) &&
								sameDate(g) &&
								sameCertificateIdentity(g.notes, send.notes)
						);
						if (matches.length) {
							// there should only be one match, alert otherwise??
							let match = matches.length == 1 ? matches[0] : scoreMatches(send, matches);
							match.tranche = c + '-' + d;
							// Add the MATCH to historic holdings
							tagMatch(match, 'A');
							historicHoldings.push(match);
							// Remove the MATCH from current gift transactions
							giftTransactions = giftTransactions.filter((g) => g.nrid != match.nrid);
							carryForwardBasisFields(send, match);
							// Add the SEND to prior transfers
							addPriorTransfer(send);
							console.log('SEND Transaction: ', send);
						}
					}
				});
				return matchCount > 0;
			}

			function canceledCertificate(sameDateSends) {
				// First, see whether this path applies at all
				let foundAny = sameDateSends.some((send) => {
					let exactMatches = giftTransactions.filter(
						(g) =>
							quantitiesMatch(send.quantity, g.quantity) &&
							sameClassAndReceiver(g) &&
							earlierDate(g) &&
							eligibleAsOf(g, send.date) &&
							sameCertificate(g.notes, send.notes)
					);
					let exactMatch = !exactMatches ? null : exactMatches.length == 1 ? exactMatches[0] : scoreMatches(send, exactMatches);
					return !!exactMatch;
				});

				if (!foundAny) return false;

				// Process the whole send set ONCE
				sameDateSends.forEach((s) => {
					s.tranche = c + '-' + d;
					giftTransactions = giftTransactions.filter((g) => g.nrid != s.nrid);

					let matches = giftTransactions.filter(
						(g) => sameClassAndReceiver(g) && earlierDate(g) && eligibleAsOf(g, s.date) && sameCertificate(g.notes, s.notes)
					);

					if (!matches.length) {
						matches = giftTransactions.filter(
							(g) => quantitiesMatch(s.quantity, g.quantity) && sameClassAndReceiver(g) && sameDate(g) && sameCertificate(g.notes, s.notes)
						);
					}

					if (matches.length) {
						let match = matches.length == 1 ? matches[0] : scoreMatches(s, matches);
						let sendQty = Number(s.quantity);
						let matchQty = Number(match.quantity);
						// #6144j: only archive the portion this send needs -- a same-cert match bigger
						// than the send (the norm for a partial cancellation) was being consumed whole,
						// silently dropping the untouched remainder instead of leaving it live.
						if (matchQty > sendQty) {
							let consumed = JSON.parse(JSON.stringify(match));
							consumed.quantity = sendQty.toString();
							consumed.tranche = c + '-' + d;
							tagMatch(consumed, 'A');
							historicHoldings.push(consumed);
							let orig = giftTransactions.find((g) => g.nrid == match.nrid);
							orig.quantity = (matchQty - sendQty).toString();
							carryForwardBasisFields(s, consumed);
						} else {
							match.tranche = c + '-' + d;
							tagMatch(match, 'A');
							historicHoldings.push(match);
							giftTransactions = giftTransactions.filter((g) => g.nrid != match.nrid);
							carryForwardBasisFields(s, match);
						}
					}
				});

				addPriorTransfers(sameDateSends);
				console.log('SEND Transactions: ', sameDateSends);
				return true;
			}

			// A cancellation's quantity can coincidentally match an unrelated transaction's.
			// If the cancellation names real cert(s), require the candidate to share one.
			function cancellationCertMismatch(sendNotes, candidateNotes) {
				if (!/\bcancel/i.test(sendNotes || '')) return false;
				const sendRefs = parseCertificateRefs(sendNotes);
				const candRefs = parseCertificateRefs(candidateNotes);
				const sendHasCert = sendRefs.newCert || sendRefs.sourceCerts.length;
				const candHasCert = candRefs.newCert || candRefs.sourceCerts.length;
				if (!sendHasCert || !candHasCert) return false;
				return !sameCertificate(candidateNotes, sendNotes);
			}

			function oneForOneMatches(sameDateSends) {
				// First try finding old certificates, where each exactly matches one of the Sends
				let matchCount = 0;
				// need clone of giftTransactions to handle potential duplicate quantities
				let giftClone = JSON.parse(JSON.stringify(giftTransactions));
				if (isEDM) giftClone = giftClone.filter((g) => notRestricted(g));
				sameDateSends.forEach((send) => {
					let exactMatches = giftClone.filter(
						(g) =>
							!activeCertificate(g) &&
							sameClassAndReceiver(g) &&
							earlierDate(g) &&
							eligibleAsOf(g, send.date) &&
							(notRestricted(g) || !isEDM) &&
							sameQty(g, Number(send.quantity))
					);
					let exactMatch = !exactMatches ? null : exactMatches.length == 1 ? exactMatches[0] : scoreMatches(send, exactMatches);
					console.log('EXACT Match: ', exactMatch);
					if (exactMatch) {
						giftClone = giftClone.filter((g) => g.nrid != exactMatch.nrid);
						matchCount++;
					}
				});

				if (moment.unix(Number(d)).utc().format('YYYY-MM-DD') == '2015-04-08' && c == 'C') {
					console.log('AL oneForOne matchCount:', matchCount, 'needed:', sameDateSends.length);
				}

				if (matchCount == sameDateSends.length) {
					sameDateSends.forEach((s) => {
						s.tranche = c + '-' + d;
						giftTransactions = giftTransactions.filter((g) => g.nrid != s.nrid);
						let matches = giftTransactions.filter(
							(g) =>
								!activeCertificate(g) &&
								sameClassAndReceiver(g) &&
								earlierDate(g) &&
								eligibleAsOf(g, s.date) &&
								(notRestricted(g) || !isEDM) &&
								sameQty(g, Number(s.quantity))
						);
						if (matches.length) {
							let match = matches.length == 1 ? matches[0] : scoreMatches(s, matches);
							match.tranche = c + '-' + d;
							tagMatch(match, matches.length == 1 ? 'C' : 'E', s);
							historicHoldings.push(match);
							giftTransactions = giftTransactions.filter((g) => g.nrid != match.nrid);
							carryForwardBasisFields(s, match);
						}
					});
					addPriorTransfers(sameDateSends);
					console.log('SEND Transactions: ', sameDateSends);
					return true;
				}
				return false;
			}

			// This method identifies the best combination of Prior Receives to match a collection of Sends
			// It also handles swaps and splits, if identified
			function parseTransfers() {
				let splitRatio = 1;
				if (isSwapOrSplit) {
					splitRatio = sameDateReceiveQtySum / sameDateSendQtySum;
					console.log('SPLIT Ratio: ', splitRatio);
				}
				// If stub, get stub info
				let stubs = sameDateTransactions.filter((b) => b.receiver == giftUserId);
				// let stubMatches = sameDateTransactions.filter(m => sameClassAndSender(m));
				let stubMatches = giftTransactions.filter((m) => sameClassAndSender(m) && sameDate(m));
				// Remove active certificates - Maybe...
				// stubMatches = stubMatches.filter(s => !activeCertificate(s));
				console.log('STUB Transactions: ', stubs);
				console.log('STUB Matches: ', stubMatches);

				// For each re-issue, remove the returned cert(s)
				// Find matching old cert(s), and calculate gross cost
				// Move old cert(s) to Prior list

				let foundGroupFit;
				let priorGrossQty = stubMatches.reduce((a, b) => a + Number(b.quantity), 0);
				let priorGrossCost = stubMatches.reduce((a, b) => a + Number(b.quantity) * toNumber(b.sharePrice), 0);
				let newPrice = priorGrossQty ? priorGrossCost / priorGrossQty : 0;
				console.log('PRIOR grossQty = ' + priorGrossQty);
				console.log('PRIOR grossCost = ' + priorGrossCost);
				console.log('NEW price = ' + newPrice);
				if (!sameDateReceives.length) {
					// Look for perfect match
					// find and remove the prior receive transaction(s) that sum to targetQty
					let priorTrans = giftTransactions.filter((g) => thisDateOrEarlier(g) && sameClassAndReceiver(g));
					priorTrans = priorTrans.filter((p) => eligibleAsOf(p, d));
					// Remove active certificates
					priorTrans = priorTrans.filter((p) => !activeCertificate(p));
					priorTrans = priorTrans.filter((p) => !isSameDayReissuance(p, d));
					priorTrans.sort((a, b) => (Number(a.date) > Number(b.date) ? -1 : 1)); // newest to oldest
					priorTrans.sort((a, b) => Number(b.quantity) - Number(a.quantity)); // largest to smallest
					console.log('PRIOR Transactions: ', priorTrans);
					minDiff = 999; // reset arbitrarily large starting number used in tranche analysis
					findSubset.callCount = 0;
					let perfectFit = findSubset(priorGrossQty, priorTrans);
					if (perfectFit) {
						foundGroupFit = true;
						console.log('PERFECT Fit: ', perfectFit);
						perfectFit.forEach((p) => (p.tranche = c + '-' + d));
						perfectFit.forEach((p) => tagMatch(p, perfectFit.length > 1 ? 'D' : priorTrans.length == 1 ? 'I' : 'C', stubMatches[0]));
						historicHoldings = historicHoldings.concat(perfectFit);
						let perfectNrids = new Set(perfectFit.map((item) => item.nrid));
						giftTransactions = giftTransactions.filter((g) => !perfectNrids.has(g.nrid));
						priorGrossQty = perfectFit.reduce((acc, val) => acc + Number(val.quantity), 0);
						priorGrossCost = perfectFit.reduce((acc, val) => acc + Number(val.quantity) * toNumber(val.sharePrice), 0);
					} else console.log('no further fit');

					// let thisTransaction = giftTransactions.find(g => g.nrid == trans.nrid);
					// if (thisTransaction) thisTransaction.sharePrice = (priorGrossCost / (isSwapOrSplit ? Number(thisTransaction.quantity) : priorGrossQty)).toString();
					// console.log("ADJUSTED Transaction: ", thisTransaction);
					// return perfectFit ? true : false;
				} else {
					let totalReceived = 0;
					let sourcedQty = 0; // #6144r: qty actually sourced from historicals (vs. receive-side qty)
					let targetQuantity = priorGrossQty * splitRatio;
					// #6144l: claim every receive's EXPLICIT cert match first, across the whole
					// batch, before any receive falls through to blind subset-sum below --
					// otherwise an unresolved line's blind search can eat a candidate a later,
					// explicitly-sourced line in this same batch still needs.
					sameDateReceives.forEach((receiveTran) => {
						let explicitTargetQty = isSwapOrSplit ? Number(receiveTran.quantity) / splitRatio : priorGrossQty;
						let sourced = handlePerfectFit(receiveTran, explicitTargetQty, Number(d), isSwapOrSplit, true);
						if (sourced) {
							totalReceived += Number(receiveTran.quantity);
							sourcedQty += Number(sourced);
							receiveTran.tranche = c + '-' + d;
							foundGroupFit = true;
						}
					});
					// #6144q: smallest-target-first -- a same-date batch's blind subset-sum searches
					// share one candidate pool, and findSubset() just returns the first exact-sum match
					// it finds, so a larger target processed first can consume candidates a smaller
					// sibling target actually needed (confirmed against RAT 2011 GST Exempt Irrevocable
					// Trust's real data: descending order let the 2704 target grab 8 lots including ones
					// the 769 target could only be built from, leaving 769 unmatched). Ascending order
					// resolves the more-constrained (smaller) targets while the pool is still whole.
					[...sameDateReceives]
						.sort((a, b) => Number(a.quantity) - Number(b.quantity))
						.forEach((receiveTran) => {
							if (receiveTran.tranche) return; // already claimed above
							// #6144r: buy-backs (receives < sends) can never satisfy totalReceived>=targetQuantity,
							// so every remaining receive ran its own full-target blind search. Compare sourced qty
							// instead for that case; swap/split path (Walt/Mazzetti below) is untouched.
							let groupSatisfied = isSwapOrSplit ? totalReceived >= targetQuantity : sourcedQty >= targetQuantity;
							if (foundGroupFit && groupSatisfied) return;
							// if (foundGroupFit) return; <-- FAILS with Walt at Mazzetti
							if (isSwapOrSplit) {
								priorGrossQty = Number(receiveTran.quantity) / splitRatio;
								console.log('SPLIT grossQty = ' + priorGrossQty);
							}
							let sourced = handlePerfectFit(receiveTran, priorGrossQty, Number(d), isSwapOrSplit);
							if (sourced) {
								totalReceived += Number(receiveTran.quantity);
								sourcedQty += Number(sourced);
								console.log('TOTAL received = ' + totalReceived, ' SOURCED qty = ' + sourcedQty);
								receiveTran.tranche = c + '-' + d;
								foundGroupFit = true;
							}
						});
				}
				if (foundGroupFit) {
					// remove all of the associated send transactions
					let sendTransactions = giftTransactions.filter((g) => sameClassAndSender(g) && sameDate(g));
					let sendSum = sendTransactions.reduce((a, b) => a + Number(b.quantity), 0);
					console.log('PRIOR GrossQty = ' + priorGrossQty, ' SEND Sum = ' + sendSum);
					sendTransactions.forEach((s) => (s.tranche = c + '-' + d));
					addPriorTransfers(sendTransactions);
					console.log('SEND Transactions: ', sendTransactions);
					giftTransactions = giftTransactions.filter((g) => !sameClassAndSender(g) || !sameDate(g));
				}
				return foundGroupFit ? true : false;
			}

			// Utility functions
			function toIssuer(trans) {
				return (
					trans.receiver == client ||
					String(trans.receiver || '').toLowerCase() == 'issuer' ||
					String(trans.receiverName || '').toLowerCase() == 'issuer' ||
					itsTreasury(trans.receiver, trans.receiverName)
				);
			}
			function fromIssuer(trans) {
				return (
					trans.sender == client ||
					String(trans.sender || '').toLowerCase() == 'issuer' ||
					String(trans.senderName || '').toLowerCase() == 'issuer' ||
					itsTreasury(trans.sender, trans.senderName)
				);
			}
			function thisDateOrEarlier(trans) {
				return Number(trans.date) <= Number(d);
			}
			function earlierDate(trans) {
				return Number(trans.date) < Number(d);
			}
			function sameDate(trans) {
				return Number(trans.date) == Number(d);
			}
			function sameQty(trans, qty) {
				return Number(trans.quantity) == qty;
			}

			function isReleaseLike(trans) {
				const s = trans.notes;
				if (!s) return false;
				const t = s.toLowerCase();
				// specific phrase first
				if (/release\s+of\s+unvested\s+shares\s+granted/.test(t)) return true;
				// broader release signal
				if (t.includes('release') && (t.includes('unvested') || t.includes('restricted'))) return true;
				return false;
			}

			// Check historicHoldings for negative quantities
			historicHoldings.forEach((h, i) => {
				if (Number(h.quantity) < 0) alert(`Negative quantity in historicHoldings[${i}]:`, h);
			});

			// Check giftTransactions for negative quantities
			giftTransactions.forEach((g, i) => {
				if (Number(g.quantity) < 0) alert(`Negative quantity in giftTransactions[${i}]:`, g);
			});
		});

		function allocateCarryOver() {
			let remaining = -carryOverDeduct; // Make positive for internal logic
			while (remaining > 0.0001) {
				// Step 1: get valid candidates with quantity > 0
				const candidates = giftTransactions.filter((gt) => {
					const qty = Number(gt.quantity);
					return !isNaN(qty) && qty > 0 && eligibleAsOf(gt, gt.date || 0);
				});

				// Step 2: pick tranche already being reduced
				let target = candidates.find((gt) => Number(gt.before) > Number(gt.quantity));

				// Step 3: otherwise pick by lowest basis + oldest date
				if (!target) {
					candidates.sort((a, b) => {
						const basisA = isKnownNumber(a._effectiveTaxBasis) ? Number(a._effectiveTaxBasis) : Infinity;
						const basisB = isKnownNumber(b._effectiveTaxBasis) ? Number(b._effectiveTaxBasis) : Infinity;
						if (basisA !== basisB) return basisA - basisB;
						return Number(a.date || 0) - Number(b.date || 0);
					});
					target = candidates[0];
				}

				if (!target) break; // Nothing valid left

				// Initialize .before if not already set
				if (target.before == null) target.before = Number(target.quantity);

				const qty = Number(target.quantity);
				const deductNow = Math.min(remaining, qty);

				target.quantity = (qty - deductNow).toFixed(6).replace(/\.?0+$/, '');
				remaining -= deductNow;
			}
		}

		// Must account for any remaining carryOverDeduct
		if (carryOverDeduct) allocateCarryOver();

		// For Edmund Optics, never include SARS, RSGs, or Awards
		if (isEDM) giftTransactions = giftTransactions.filter((g) => notRestricted(g));

		function carryForwardBasisFields(targetTrans, sourceTrans) {
			if (!sourceTrans) return;
			// #6144: tag when a value here didn't come from the transaction's own record -- feeds the 'F' Key.
			let carried = false;
			if (!isKnownNumber(targetTrans.sharePrice) && isKnownNumber(sourceTrans.sharePrice)) {
				targetTrans.sharePrice = sourceTrans.sharePrice;
				carried = true;
			}
			if (!isKnownNumber(targetTrans.taxBasis)) {
				if (isKnownNumber(sourceTrans.taxBasis)) {
					targetTrans.taxBasis = sourceTrans.taxBasis;
					carried = true;
				} else if (isKnownNumber(sourceTrans._effectiveTaxBasis)) {
					targetTrans.taxBasis = Number(sourceTrans._effectiveTaxBasis).toString();
					carried = true;
				}
			}
			if (carried) targetTrans._basisCarried = true;
			let basis = resolveEffectiveTaxBasis(targetTrans, clientData);
			targetTrans._effectiveTaxBasis = basis.value;
			targetTrans._basisSource = basis.source;
			console.log('ADJUSTED Transaction: ', targetTrans);
		}

		// #6144g: a same-day Reissuance is disqualified as a candidate when it's
		// matched by a same-day Cancellation of the SAME quantity -- that's the
		// signature of a cancel/reissue re-cert pair (quantitiesMatch, not a bare
		// date check), and the true source is always whatever cert existed BEFORE
		// that pair, never the reissuance itself -- so excluding it never risks
		// losing the real source, only a same-day proxy for it.
		function isSameDayReissuance(candidate, date) {
			if (Number(candidate.date) !== Number(date)) return false;
			if (!/re-?issu/i.test(candidate.description || '')) return false;
			return giftTransactions.some(
				(g) =>
					g.nrid !== candidate.nrid &&
					g.sender === giftUserId &&
					g.shareClass === candidate.shareClass &&
					Number(g.date) === Number(date) &&
					/cancel/i.test(g.description || '') &&
					quantitiesMatch(g.quantity, candidate.quantity)
			);
		}

		// This method looks for one or more prior transactions specified precisely in the send notes
		function handleNotesMatch(trans, targetQty, date, contestedCerts) {
			let priorTrans = giftTransactions.filter((g) => Number(g.date) <= date && sameClassAndReceiver(g));
			priorTrans = priorTrans.filter((p) => eligibleAsOf(p, date));
			priorTrans = priorTrans.filter((p) => !isSameDayReissuance(p, date));

			// Consume explicitly named lots (cert or dated book entry); commits only if they cover the send.
			function consumeExplicitLots(lotMatches) {
				let remainingTarget = Number(targetQty) || 0;
				const stagedItems = [];
				const stagedUpdates = [];

				lotMatches.forEach((match) => {
					if (remainingTarget <= 0) return;

					const priorGrossQty = Number(match.quantity) || 0;
					const consumeQty = Math.min(remainingTarget, priorGrossQty);

					stagedItems.push({
						...match,
						tranche: c + '-' + trans.date,
						quantity: consumeQty.toString(),
					});

					stagedUpdates.push({
						nrid: match.nrid,
						remaining: priorGrossQty - consumeQty,
						grossQty: priorGrossQty,
					});

					remainingTarget -= consumeQty;
				});

				// IMPORTANT: commit only if the explicit cert list fully satisfies the send
				if (remainingTarget <= 0) {
					stagedItems.forEach((p) => tagMatch(p, 'A'));
					historicHoldings = historicHoldings.concat(stagedItems);

					stagedUpdates.forEach((u) => {
						// Epsilon-tolerant, not <= 0 -- float noise can leave remaining ~1e-16.
						if (u.remaining <= balanceTolerance(u.grossQty)) {
							giftTransactions = giftTransactions.filter((g) => g.nrid != u.nrid);
						} else {
							const idx = giftTransactions.findIndex((g) => g.nrid == u.nrid);
							if (idx >= 0) giftTransactions[idx].quantity = u.remaining.toString();
						}
					});

					let thisTransaction = giftTransactions.find((g) => g.nrid == trans.nrid);
					if (thisTransaction) carryForwardBasisFields(thisTransaction, lotMatches[0]);
					return true;
				}

				return false;
			}

			// NEW: explicit source-cert handling
			const explicitSourceCerts = CERT_COLUMN ? getExplicitSourceCertSet(trans.notes) : null;
			if (explicitSourceCerts) {
				let certMatches = priorTrans.filter((p) => {
					const lotCert = getLotCertToken(p.notes);
					if (!lotCert || !explicitSourceCerts.has(lotCert)) return false;
					// #6144n: a sibling same-day Receive already claims this cert by its own notes.
					if (contestedCerts && contestedCerts.has(lotCert)) return false;
					return true;
				});
				if (certMatches.length) {
					certMatches.sort((a, b) => Number(a.date) - Number(b.date));
					// If cert-linked lots are insufficient, do NOTHING here and let later logic handle it
					return consumeExplicitLots(certMatches);
				}
			}

			// Book-entry lots have no cert; "Book Entry dated M/D/YY" names the lot by its date.
			const bookEntryDate = CERT_COLUMN ? getBookEntrySourceDate(trans.notes, trans.date) : null;
			if (bookEntryDate) {
				const beMatches = priorTrans.filter(
					(p) =>
						isBookEntryLot(p) &&
						(moment.unix(Number(p.date)).utc().format('YYYY-MM-DD') === bookEntryDate || getBookEntrySourceDate(p.notes, p.date) === bookEntryDate)
				);
				if (beMatches.length) {
					const exact = (p) => (quantitiesMatch(p.quantity, targetQty) ? 0 : 1);
					beMatches.sort((a, b) => exact(a) - exact(b) || Number(a.date) - Number(b.date));
					if (consumeExplicitLots(beMatches)) return true;
				}
			}

			// EXISTING behavior for simpler notes
			let notesMatch = findNotesMatch(trans.notes, priorTrans);
			if (notesMatch) {
				console.log('NOTES MATCH: ', notesMatch);

				const priorGrossQty = Number(notesMatch.quantity) || 0;
				const consumeQty = Math.min(Number(targetQty) || 0, priorGrossQty);

				// Clone BEFORE pushing so we don't mutate the pushed reference
				const trancheItem = {
					...notesMatch,
					tranche: c + '-' + trans.date,
					quantity: consumeQty.toString(),
				};
				// Immutable List -> reassign
				// #6144f: only one eligible candidate -> 'I' (O.P.), not 'B' -- no note needed to be sure.
				tagMatch(trancheItem, priorTrans.length == 1 ? 'I' : 'B');
				historicHoldings.push(trancheItem);

				// Update/remove the ORIGINAL in giftTransactions
				const remaining = priorGrossQty - consumeQty;
				// #6144-follow-up: same epsilon-tolerant check as above -- see comment there.
				if (remaining <= balanceTolerance(priorGrossQty)) {
					giftTransactions = giftTransactions.filter((g) => g.nrid != notesMatch.nrid);
				} else {
					const idx = giftTransactions.findIndex((g) => g.nrid == notesMatch.nrid);
					if (idx >= 0) giftTransactions[idx].quantity = remaining.toString();
				}

				// Carry price to the current transaction
				let thisTransaction = giftTransactions.find((g) => g.nrid == trans.nrid);
				if (thisTransaction) carryForwardBasisFields(thisTransaction, notesMatch);
				return true;
			}

			console.log('no notes match');
			return false;
		}

		function getExplicitSourceCertSet(notesText) {
			const refs = parseCertificateRefs(notesText);
			return refs.sourceCerts?.length ? new Set(refs.sourceCerts) : null;
		}

		function getLotCertToken(notesText) {
			const refs = parseCertificateRefs(notesText);

			// For old lots like "Issuance (Cert 0002475)", use the cert on the lot itself
			if (refs.newCert) return refs.newCert;

			// If somehow the lot only parses as a single source cert, allow that too
			if (refs.sourceCerts?.length == 1) return refs.sourceCerts[0];

			return '';
		}

		// "Book Entry dated 6/27/08" -> '2008-06-27'. Two-digit years can't land after the send itself.
		function getBookEntrySourceDate(notesText, asOf) {
			const m = String(notesText || '').match(/book\s*entry\s+dated\s+(\d{1,2}\/\d{1,2}\/\d{2,4})\b/i);
			if (!m) return null;
			let dt = moment.utc(m[1], ['M/D/YYYY', 'M/D/YY'], true);
			if (!dt.isValid()) return null;
			if (asOf && dt.unix() > Number(asOf)) dt = dt.subtract(100, 'years');
			return dt.format('YYYY-MM-DD');
		}

		function isBookEntryLot(p) {
			return /book\s*entry/i.test(p?.notes || '') && !getLotCertToken(p.notes);
		}

		function findNotesMatch(transNotes, priorTrans) {
			console.log('transNotes', transNotes);

			const tn = String(transNotes || '').trim();
			const tnLower = tn.toLowerCase();

			// Hard gate: only match notes that explicitly say "... from ...", to avoid
			// accidental substring matches on busy accounts.
			const hasFromPhrase = /\bfrom\b\s*[:\-]?\s+\S+/i.test(tn);
			if (!hasFromPhrase) {
				console.log("findNotesMatch: skipping (no '... from ...' phrase)");
				return false;
			}

			function extractPossibleDate(str) {
				if (!str) return null;
				const dateRegex = /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\.?\s*\d{1,2},?\s*\d{2,4}|\b\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}\b/i;
				const match = str.match(dateRegex);
				return match ? match[0] : null;
			}

			const possibleDateString = extractPossibleDate(tn);
			const transMoment = possibleDateString ? moment(possibleDateString, dateFormats, true) : null;

			for (const t of priorTrans) {
				if (!t || !t.notes || !t.date) continue;

				const priorNotes = String(t.notes || '').trim();
				if (!priorNotes) continue;

				// Keep your original "notes contain prior notes" behavior
				const notesHit = tnLower.includes(priorNotes.toLowerCase());
				if (!notesHit) continue;

				let dateMatch = true; // allow notes-only when no parseable date
				if (transMoment && moment.unix(Number(t.date)).utc().isValid()) {
					const tMoment = moment.unix(Number(t.date)).utc();
					dateMatch = tMoment.isSame(transMoment, 'day');
				}

				if (dateMatch) {
					console.log('Matched prior transaction:', t);
					return t; // <- returns a single OBJECT
				}
			}

			return false;
		}

		// Character overlap similarity (basic fuzzy match)
		function areSimilar(a, b) {
			const setA = new Set(a.toLowerCase());
			const setB = new Set(b.toLowerCase());
			const overlap = [...setA].filter((c) => setB.has(c)).length;
			const avgLen = (setA.size + setB.size) / 2;
			return overlap / avgLen;
		}

		// true if this prior lot is eligible to be consumed as-of "asOf"
		function eligibleAsOf(priorLot, asOf) {
			const u = priorLot?._useAfter;
			if (!u) return true;
			if (u === 'LOCK') return false;

			const asOfSec = Number(asOf);
			const useAfterSec = Number(u);

			if (!Number.isFinite(asOfSec) || !Number.isFinite(useAfterSec)) return false;

			return asOfSec >= useAfterSec;
		}

		function sameCertificate(transNotes, notes) {
			if (!CERT_COLUMN) return areSimilar(transNotes, notes);

			// PRIOR EFFORTS
			// console.log("transNotes", (transNotes.match(/\(([^)]*?(\d+)[^)]*?)\)/) || [])[2])
			// console.log("notes", (notes.match(/\(([^)]*?(\d+)[^)]*?)\)/) || [])[2])
			// return (transNotes.match(/\((\d+)\)/) || [])[1] == (notes.match(/\((\d+)\)/) || [])[1];
			// if ((transNotes.match(/\(([^)]*?(\d+)[^)]*?)\)/) || [])[2] == (notes.match(/\(([^)]*?(\d+)[^)]*?)\)/) || [])[2]) {
			// 	console.log("***MATCH***");
			// 	return true;
			// } else return false;

			// Extract a cert token from the first (...) group; must contain a digit
			// (so "(Cancelled)" etc. don't match).
			const a = parseCertificateRefs(transNotes);
			const b = parseCertificateRefs(notes);

			// Best case: overlap of source cert lists
			if (a.sourceCerts.length || b.sourceCerts.length) {
				const aSet = new Set(a.sourceCerts.length ? a.sourceCerts : a.newCert ? [a.newCert] : []);
				const bSet = new Set(b.sourceCerts.length ? b.sourceCerts : b.newCert ? [b.newCert] : []);

				for (const cert of aSet) {
					if (bSet.has(cert)) {
						console.log('***MATCH via source overlap***');
						return true;
					}
				}
				return false;
			}

			// Simple fallback: compare single certs
			if (a.newCert && b.newCert && a.newCert == b.newCert) {
				console.log('***MATCH via single cert***');
				return true;
			}

			return false;
		}

		// This method takes a given Send and tries to find a collection of Prior Receives that fits perfectly
		// #6144g: prefer an exact single-cert match before falling back to findSubset's
		// notes-blind cross-cert search below, which can settle for a close-but-wrong
		// multi-cert split when a single cert's own holdings already sum exactly.
		function findSingleCertExactMatch(targetQty, candidates) {
			const groups = {};
			candidates.forEach((c) => {
				const token = getLotCertToken(c.notes);
				if (!token) return;
				(groups[token] = groups[token] || []).push(c);
			});
			for (const token in groups) {
				const group = groups[token];
				const sum = group.reduce((a, b) => a + Number(b.quantity), 0);
				if (Math.abs(sum - targetQty) < 0.000001) return group;
			}
			return null;
		}

		// #6144k: prefer candidates the SEND's own notes explicitly name as source certs
		// (e.g. "Cancellation (10484 from 10484)") over blind quantity-only matching --
		// clients with PREFER_CERT_LINEAGE off (e.g. Asplundh) only reach cert info here.
		//
		// #6144m: a legacy cert can legitimately fund more than one new cert (e.g. a
		// tax-basis-driven reissuance that splits one surrendered cert into several stub
		// lots, same real cert # entered as separate rows with their own quantities). The
		// full-group sum below only covers one-cert-to-one-reissuance; findSubsetSum below
		// lets a subset of same-cert rows satisfy the target instead of requiring the whole
		// named group to sum exactly.
		function findExplicitCertMatch(transNotes, targetQty, candidates) {
			const explicit = candidates.filter((c) => sameCertificate(transNotes, c.notes));
			if (!explicit.length) {
				return null;
			}
			const sum = explicit.reduce((a, b) => a + Number(b.quantity), 0);
			if (Math.abs(sum - targetQty) < 0.000001) {
				return explicit;
			}
			if (explicit.length <= 1) {
				return null;
			}
			const subsetResult = findSubsetSum(explicit, targetQty);
			return subsetResult;
		}

		// #6144o: a single, UNAMBIGUOUSLY-named source cert holding more than the target --
		// a partial draw from a larger historical lot. findExplicitCertMatch above only
		// returns an exact-sum group, so it can't represent "take part of this one row,
		// leave the rest" -- handlePerfectFit handles that case using this instead.
		// Deliberately stricter than findExplicitCertMatch's sameCertificate() (source-set
		// overlap, which can match on any one of several named certs): this only fires when
		// transNotes names EXACTLY ONE source cert and EXACTLY ONE candidate carries that
		// exact token, so it can't misfire on a multi-cert citation where a sibling cert was
		// already claimed elsewhere and just happens to leave a single unrelated candidate.
		function findExplicitCertPartial(transNotes, targetQty, candidates) {
			const soleCerts = getExplicitSourceCertSet(transNotes);
			if (!soleCerts || soleCerts.size !== 1) return null;
			const [cert] = soleCerts;
			const matches = candidates.filter((c) => getLotCertToken(c.notes) === cert);
			if (matches.length !== 1) return null;
			const avail = Number(matches[0].quantity) || 0;
			if (avail <= Number(targetQty) + 0.000001) return null;
			return matches[0];
		}

		// Bounded brute-force subset-sum -- a same-cert group is always a handful of
		// historicals rows, never large, so this stays cheap and auditable. Returns the
		// first exact-sum subset found; if more than one subset fits, the choice is
		// arbitrary but deterministic (candidate order), same as any other tie.
		function findSubsetSum(items, targetQty) {
			if (items.length > 24) {
				return null;
			}
			const n = items.length;
			for (let mask = 1; mask < 1 << n; mask++) {
				let sum = 0;
				for (let i = 0; i < n; i++) if (mask & (1 << i)) sum += Number(items[i].quantity);
				if (Math.abs(sum - targetQty) < 0.000001) return items.filter((_, i) => mask & (1 << i));
			}
			return null;
		}

		function handlePerfectFit(trans, targetQty, date, isSwapOrSplit, explicitOnly) {
			// Look for perfect match
			let priorGrossQty = 0;
			let priorGrossCost = 0;
			// find and remove the prior receive transaction(s) that sum to targetQty
			let priorTrans = giftTransactions.filter((g) => Number(g.date) <= date && sameClassAndReceiver(g));
			priorTrans = priorTrans.filter((p) => eligibleAsOf(p, date));
			// Remove active certificates
			priorTrans = priorTrans.filter((p) => !activeCertificate(p));
			priorTrans = priorTrans.filter((p) => !isSameDayReissuance(p, date));
			// Remove restricted stock
			if (isEDM) priorTrans = priorTrans.filter((p) => !p.notes.startsWith('Restricted'));
			// Sort according to priority
			if (isVERM)
				priorTrans.sort((a, b) => (Number(a.date) > Number(b.date) ? 1 : -1)); // oldest to newest
			else {
				priorTrans.sort((a, b) => (Number(a.date) > Number(b.date) ? -1 : 1)); // newest to oldest
				priorTrans.sort((a, b) => Number(b.quantity) - Number(a.quantity)); // largest to smallest
			}
			console.log('PRIOR Transactions: ', priorTrans);
			let olderPriorTrans = priorTrans.filter((g) => Number(g.date) < date);
			// #6144o: a single named cert holding more than this target needs -- handle the
			// partial draw directly, before the exact-sum-only chain below gets a chance to
			// (wrongly) report no fit just because the lot is bigger than what's needed.
			let partialCert = findExplicitCertPartial(trans.notes, targetQty, olderPriorTrans) || findExplicitCertPartial(trans.notes, targetQty, priorTrans);
			let perfectFit;
			if (partialCert) {
				const consumeQty = Number(targetQty) || 0;
				const avail = Number(partialCert.quantity) || 0;
				const remaining = avail - consumeQty;
				const consumed = { ...partialCert, tranche: c + '-' + trans.date, quantity: consumeQty.toString() };
				tagMatch(consumed, priorTrans.length == 1 ? 'I' : 'C', trans);
				console.log('PARTIAL Fit: ', consumed);
				historicHoldings = historicHoldings.concat([consumed]);
				if (remaining <= balanceTolerance(avail)) {
					giftTransactions = giftTransactions.filter((g) => g.nrid != partialCert.nrid);
				} else {
					const idx = giftTransactions.findIndex((g) => g.nrid == partialCert.nrid);
					if (idx >= 0) giftTransactions[idx].quantity = remaining.toString();
				}
				priorGrossQty = consumeQty;
				priorGrossCost = consumeQty * toNumber(partialCert.sharePrice);
				perfectFit = [consumed];
			} else {
				// first try with only older transactions
				minDiff = 999; // reset arbitrarily large starting number used in tranche analysis
				findSubset.callCount = 0;
				perfectFit =
					findExplicitCertMatch(trans.notes, targetQty, olderPriorTrans) ||
					(explicitOnly ? null : findSingleCertExactMatch(targetQty, olderPriorTrans) || findSubset(targetQty, olderPriorTrans));
				// now include same-date transactions
				minDiff = 999; // reset arbitrarily large starting number used in tranche analysis
				findSubset.callCount = 0;
				if (!perfectFit)
					perfectFit =
						findExplicitCertMatch(trans.notes, targetQty, priorTrans) ||
						(explicitOnly ? null : findSingleCertExactMatch(targetQty, priorTrans) || findSubset(targetQty, priorTrans));
				if (perfectFit) {
					console.log('PERFECT Fit: ', perfectFit);
					perfectFit.forEach((p) => (p.tranche = c + '-' + trans.date));
					perfectFit.forEach((p) => tagMatch(p, perfectFit.length > 1 ? 'D' : priorTrans.length == 1 ? 'I' : 'C', trans));
					historicHoldings = historicHoldings.concat(perfectFit);
					let perfectNrids = new Set(perfectFit.map((item) => item.nrid));
					giftTransactions = giftTransactions.filter((g) => !perfectNrids.has(g.nrid));
					priorGrossQty = perfectFit.reduce((acc, val) => acc + Number(val.quantity), 0);
					priorGrossCost = perfectFit.reduce((acc, val) => acc + Number(val.quantity) * toNumber(val.sharePrice), 0);
				} else console.log('no further fit');
			}

			let thisTransaction = giftTransactions.find((g) => g.nrid == trans.nrid);
			if (thisTransaction) thisTransaction.sharePrice = (priorGrossCost / (isSwapOrSplit ? Number(thisTransaction.quantity) : priorGrossQty)).toString();
			console.log('ADJUSTED Transaction: ', thisTransaction);
			// #6144r: report qty sourced, not just bool, for group-target tracking below.
			return perfectFit ? priorGrossQty : false;
		}

		// This subroutine finds a subset of Receives that sum exactly to a target quantity
		function findSubset(targetQty, priorTrans, subset = [], index = 0, currentSum = 0) {
			if (!findSubset.callCount) findSubset.callCount = 0;
			findSubset.callCount++;
			if (findSubset.callCount > 500000) return null; // hard stop

			// tolerate tiny decimal noise
			if (Math.abs(currentSum - targetQty) < 0.000001) return subset;

			let diff = Math.abs(targetQty - currentSum);
			if (diff < minDiff) {
				minDiff = diff;
				diffCount = 0;
				console.log('minDiff = ' + minDiff);
			}
			diffCount++;

			if (diff < 1 && diffCount > 99) return subset;

			if (currentSum > targetQty || index >= priorTrans.length) return null;

			let qty = Number(priorTrans[index].quantity) || 0;

			let includeCurrent = findSubset(targetQty, priorTrans, [...subset, priorTrans[index]], index + 1, currentSum + qty);
			if (includeCurrent) return includeCurrent;

			return findSubset(targetQty, priorTrans, subset, index + 1, currentSum);
		}

		// This method scores potential matches, and returns the one with the highest score
		function scoreMatches(transfer, historics) {
			let bestMatch;
			let bestScore = 0;
			if (historics.length) bestMatch = historics[0];
			historics.forEach((h) => {
				let score = areSimilar(transfer.notes, h.notes);
				// High-priority sender-declared-basis signal (2026-08-28, per Chris,
				// GT improvements #6144) -- outweighs the exact-quantity match (7)
				// below since an explicit sender assertion is more trustworthy than
				// any heuristic here.
				if (senderBasisMatchesCandidate(transfer, h)) score += 10;
				if (transfer.sharePrice == h.sharePrice) score += 3;
				if (quantitiesMatch(transfer.quantity, h.quantity)) score += 7;
				if (transfer.notes.toLowerCase().includes(h.notes.toLowerCase())) score += 5;
				// $0 transfers (gifts/distributions) have no real sale price to compare against --
				// this guard only makes sense for an actual paid transfer.
				if (Number(transfer.sharePrice) > 0 && Number(transfer.sharePrice) < Number(h.sharePrice)) score = 0;
				if (Number(h.quantity) > Number(transfer.quantity)) score += 1;
				if (Number(h.quantity) < Number(transfer.quantity)) score = 0;
				if (Number(transfer.date) < Number(h.date)) score = 0;
				// if (activeCertificate(h)) score = 0;
				if (score > bestScore) {
					bestScore = score;
					bestMatch = h;
				}
			});
			return bestMatch;
		}

		function scoreDifferences(transfer, historics) {
			let bestMatch;
			let bestScore = -Infinity; // allow 0 scores
			if (historics.length) bestMatch = historics[0];

			historics.forEach((h) => {
				let score = areSimilar(transfer.notes, h.notes);
				if (score > 0.9) score = 0; // handle EO Releases

				// Extract dates from notes
				const transferNoteDate = extractDate(transfer.notes);
				const historicNoteDate = extractDate(h.notes);

				// Strongly prefer a note-date ≈ record .date
				const dateBoost =
					(transferNoteDate && isCloseInTime(transferNoteDate, h.date) ? 1.0 : 0) +
					(historicNoteDate && isCloseInTime(historicNoteDate, transfer.date) ? 0.5 : 0);

				// Small bonus if share prices match (helps pick the Grant @ 318.03)
				const priceBoost = +transfer.sharePrice && +h.sharePrice && Math.abs(+transfer.sharePrice - +h.sharePrice) < 0.005 ? 0.5 : 0;

				// High-priority sender-declared-basis signal (2026-08-28, per Chris,
				// GT improvements #6144) -- outweighs dateBoost/priceBoost above.
				const basisBoost = senderBasisMatchesCandidate(transfer, h) ? 2.0 : 0;

				// Down-weight loose text similarity; up-weight structured signals
				const totalScore = score * 0.3 + dateBoost + priceBoost + basisBoost;

				if (totalScore > bestScore) {
					bestScore = totalScore;
					bestMatch = h;
				}
			});

			return bestMatch;

			// Extract timestamp (ms) from note string if any recognizable date found
			function extractDate(text) {
				const match = text.match(/(\d{1,4}[\/\-]\d{1,2}[\/\-]\d{1,4}|\w{3} \d{1,2}, \d{4})/);
				if (match) {
					const m = moment(match[0], dateFormats, true);
					if (m.isValid()) return m.valueOf(); // ms since epoch
				}
				return null;
			}

			// True if two dates are within ±5 days (input: ms, seconds)
			function isCloseInTime(msFromNote, dateStampSeconds) {
				const dateFromItem = Number(dateStampSeconds) * 1000;
				const delta = Math.abs(msFromNote - dateFromItem);
				return delta <= 5 * 24 * 60 * 60 * 1000; // ±5 days in ms
			}
		}

		function notRestricted(trans) {
			const note = trans.notes?.toLowerCase() || '';
			return !['restricted stock grant', 'stock award', 'appreciation rights grant'].some((term) => note.includes(term));
		}

		function activeCertificate(trans) {
			return /\(.*?#.*?\)/.test(trans.notes);
		}

		function sameClassAndSender(trans) {
			return trans.shareClass == c && trans.sender == giftUserId;
		}

		function sameClassAndReceiver(trans) {
			return trans.shareClass == c && trans.receiver == giftUserId;
		}
	});

	return { giftTransactions, historicHoldings };
}

// Dollar classes: Events (accruals, distributions...) move the balance but never create ledger rows.
const EVENT_BALANCE_SIGN = {
	Contribution: 1,
	'Gain (Loss) Accrual': 1,
	'Principal Funded': 1,
	'Interest Accrual': 1,
	Distribution: -1,
	Redemption: -1,
	'Return of Capital': -1,
	'Principal Repayment': -1,
};

// Dated, signed Events for one holder's dollar class: [{ day, sec, amount }].
function holderClassEvents(clientData, holderClass, classKey) {
	const clientEvents = new Map((clientData?.equity?.[classKey]?.events || []).map((e) => [e.nrid, e]));
	return (holderClass?.events || []).flatMap((a) => {
		const ce = clientEvents.get(a.nrid);
		const amount = (EVENT_BALANCE_SIGN[ce?.eventType] || 0) * (Number(a.grossDollars) || 0);
		if (!amount) return [];
		const sec = Number(ce.record_date || ce.date);
		return [{ day: Math.floor(sec / 86400), sec, amount }];
	});
}

// L&L: size of the event-funded lot (net Events), only when the ledger is short (sent > received) or the class has
// no ledger rows, and approved = net Events + received - sent to the penny. Otherwise 0, and the account flags as before.
export function eventFundedLotSize(approved, netEvents, received, sent, hasLedgerRows = true) {
	if ((hasLedgerRows && !(sent - received > 0.5)) || !(netEvents > 0)) return 0;
	const reconciles = Math.abs(approved - (netEvents + received - sent)) <= Math.max(0.05, Math.abs(approved) * 1e-6);
	return reconciles ? +netEvents.toFixed(4) : 0;
}

// Replays dated Events pro rata onto lots ({ day, qty }; events { day, amount }, signed). Same-day lots
// enter before events. Returns new quantities, or null unless they total the cap-table balance.
export function replayEventsOntoLots(lots, events, approved) {
	const bal = lots.map(() => 0);
	const steps = lots.map((l, i) => ({ day: l.day, order: 0, i, qty: l.qty }));
	events.forEach((e) => steps.push({ day: e.day, order: 1, amount: e.amount }));
	steps.sort((a, b) => a.day - b.day || a.order - b.order);
	for (const st of steps) {
		if (st.order === 0) {
			bal[st.i] = st.qty;
			continue;
		}
		const total = bal.reduce((sum, q) => sum + q, 0);
		if (total <= 0) return null; // Event before any capital exists: can't place it
		bal.forEach((q, i) => (bal[i] = q + (st.amount * q) / total));
	}
	const total = bal.reduce((sum, q) => sum + q, 0);
	return Math.abs(total - approved) <= Math.max(0.01, Math.abs(approved) * 1e-6) ? bal : null;
}

/**
 * #6602: a holder's tranche analysis, from giftTransfer()'s gathered rows (one holder, stepwise-clamped):
 * L&L event-funded lots, effective basis, the matching ladder, then the L&L restatement and #6108 fallback.
 * Returns the remaining tranches (giftTransactions), consumed sources (historicHoldings), the pre-matching
 * snapshot (origTransactions) and the classes given an event-funded lot. priorTransfers is filled in place.
 */
export function analyzeTranches({ classes, giftTransactions, historicHoldings, priorTransfers, giftUserData, giftUserId, clientData, certColumn }) {
	let origTransactions = [];

	// L&L Products: capital funded by Events has no ledger lot, so dollar transfers out of it matched nothing.
	// Add one event-funded lot (net Events) when the ledger is short or empty and the class's books reconcile.
	const eventFundedClasses = new Set();
	if (isLLProducts) {
		const sumQty = (rows) => rows.reduce((total, g) => total + Number(g.quantity), 0);
		classes.forEach((c) => {
			if (getEffectiveDenomination(clientData, c) !== 'dollars') return;
			const holderClass = giftUserData?.clients?.[client]?.shares?.[c];
			const approved = Number(holderClass?.approved);
			if (!holderClass?.events?.length || !Number.isFinite(approved)) return;
			const sent = giftTransactions.filter((g) => g.shareClass == c && g.sender == giftUserId);
			const received = giftTransactions.filter((g) => g.shareClass == c && g.receiver == giftUserId);
			const events = holderClassEvents(clientData, holderClass, c);
			const netEvents = events.reduce((total, e) => total + e.amount, 0);
			const size = eventFundedLotSize(approved, netEvents, sumQty(received), sumQty(sent), sent.length + received.length > 0);
			if (!size) return;
			eventFundedClasses.add(c);
			giftTransactions.push({
				nrid: 'event-funded-' + c + '-' + giftUserId,
				shareClass: c,
				securityType: 'Partnership Interest',
				date: Math.min(...events.map((e) => e.sec), ...sent.map((g) => Number(g.date))),
				sender: client,
				senderName: 'Issuer',
				receiver: giftUserId,
				receiverName: giftUserData?.user_name || '',
				quantity: size.toString(),
				sharePrice: '1',
				taxBasis: '',
				notes: 'Event-funded capital (accruals and contributions; no ledger entry)',
				_eventFunded: true, // virtual lot: transferable, but never written to or updated in the ledger
			});
		});
	}

	giftTransactions.sort((a, b) => Number(a.date) - Number(b.date));
	origTransactions = JSON.parse(JSON.stringify(giftTransactions));

	// normalize all dates to midnight of that date
	giftTransactions.forEach((t) => t.date && (t.date = moment.unix(t.date).utc().startOf('day').unix()));
	giftTransactions.forEach((t) => (t._useAfter = deferUseTs(t.notes)));
	console.log(
		'LOCK lots:',
		giftTransactions.filter((t) => t._useAfter === 'LOCK')
	);

	giftTransactions.forEach((t) => {
		const basis = resolveEffectiveTaxBasis(t, clientData);
		t._effectiveTaxBasis = basis.value;
		t._basisSource = basis.source;
		console.log('EFFECTIVE BASIS = ' + basis.value + ' ' + basis.source);
	});

	// For each Recieve from company with no sharePrice, insert latest valuation or assumed price
	// giftTransactions.forEach(r => {
	// 	if (r.receiver == giftUserId && (r.sender == "" || r.sender == client || r.senderName.toLowerCase() == "issuer"))
	// 		r.sharePrice = toNumber(r.sharePrice).toString();
	// 		let classValuations = clientData.equity[r.shareClass].valuations.sort((a, b) => Number(a.date) - Number(b.date));
	// 		let nearestValuation = null;
	// 		for (const valuation of classValuations) {
	// 			if (Number(valuation.date) > Number(r.date)) break;
	// 			nearestValuation = valuation;
	// 		}
	// 		if (nearestValuation) r.sharePrice = nearestValuation.sharePrice;
	// });

	({ giftTransactions, historicHoldings } = runMatchingLadder({
		classes,
		giftTransactions,
		historicHoldings,
		priorTransfers,
		giftUserId,
		clientData,
		certColumn,
	}));

	// Dollar-denominated classes with 2+ leftover lots: restate the lots for Events so they total the cap-table
	// balance (the 1-lot case is the #6108 fallback below). Applied only when the replay reconciles exactly.
	classes.forEach((c) => {
		if (getEffectiveDenomination(clientData, c) !== 'dollars' || eventFundedClasses.has(c)) return;
		const holderClass = giftUserData?.clients?.[client]?.shares?.[c];
		const approved = Number(holderClass?.approved);
		if (!holderClass?.events?.length || !Number.isFinite(approved)) return;
		const lots = giftTransactions.filter((g) => g.shareClass == c && g.receiver == giftUserId && Number(g.quantity) > 0);
		if (lots.length < 2) return;
		const events = holderClassEvents(clientData, holderClass, c);
		const restated = replayEventsOntoLots(
			lots.map((l) => ({ day: Math.floor(Number(l.date) / 86400), qty: Number(l.quantity) })),
			events,
			approved
		);
		if (!restated) return;
		lots.forEach((l, i) => {
			l.quantity = restated[i].toString();
			l._eventRestated = true; // marks the lot for anyone inspecting the data later
		});
	});

	// #6108 (Ullico) fallback: if a class has exactly one leftover unconsumed entry
	// and its quantity disagrees with the precomputed "Shares Owned Now" balance,
	// correct that entry to match. No-op if matching fully reconciled or 2+ are left over.
	classes.forEach((c) => {
		const correctBalanceRaw = giftUserData?.clients?.[client]?.shares?.[c]?.approved;
		if (correctBalanceRaw == null) return;
		const correctBalance = Number(correctBalanceRaw);
		if (!Number.isFinite(correctBalance)) return;

		const leftover = giftTransactions.filter((g) => g.shareClass == c && g.receiver == giftUserId);
		if (leftover.length !== 1) return; // ambiguous or already empty -- leave alone

		const leftoverQty = Number(leftover[0].quantity);
		if (leftoverQty === correctBalance) return; // already reconciled, nothing to do
		if (nearlyEqualBalance(leftoverQty, correctBalance)) {
			leftover[0].quantity = correctBalance.toString(); // float noise: snap quietly, not a fallback
			return;
		}

		// A tranche can't hold more than it started with: a balance above that means the cap table is off, not the ledger.
		const initial = origTransactions.find((o) => o.nrid && o.nrid === leftover[0].nrid);
		if (initial && correctBalance > Number(initial.quantity) && !nearlyEqualBalance(correctBalance, initial.quantity)) {
			console.warn(
				`giftTransfer (#6108 fallback): ${giftUserId}/${c} cap table balance ${correctBalance} exceeds this tranche's initial ${initial.quantity} -- keeping the ledger's ${leftoverQty}; check the cap table.`
			);
			return;
		}

		console.warn(
			`giftTransfer (#6108 fallback): unreconciled tranche history for ${giftUserId}/${c} -- leftover entry shows ${leftoverQty}, precomputed balance is ${correctBalance}. Displaying the precomputed balance instead of the stale tranche quantity.`
		);
		leftover[0].quantity = correctBalance.toString();
		leftover[0]._reconciliationFallback = true; // marks this entry for anyone inspecting the data later
	});

	return { giftTransactions, historicHoldings, origTransactions, eventFundedClasses };
}

// #6602: data-only ordering and A/B/C tranche letters (the Tranche Scan and other dataOnly callers).
export function letterTranches({ giftTransactions, historicHoldings, priorTransfers }) {
	// Sort: non-zero quantities first, then zero quantities (same as renderUserGiftTransModal)
	let nonZero = giftTransactions.filter((t) => Number(t.quantity) != 0);
	let zeroQty = giftTransactions.filter((t) => Number(t.quantity) == 0).sort((a, b) => Number(b.date) - Number(a.date));
	giftTransactions = sortByClassThenBasisThenDateThenQty(nonZero).concat(zeroQty);
	historicHoldings = sortByClassThenBasisThenDateThenQty(historicHoldings);
	priorTransfers = sortByClassThenBasisThenDateThenQty(priorTransfers);

	// Sort historicHoldings by tranche (class-date format)
	historicHoldings.sort((a, b) => {
		if (!a.tranche || !b.tranche) return 0;
		let [classA, dateA] = a.tranche.toString().split('-');
		let [classB, dateB] = b.tranche.toString().split('-');
		dateA = Number(dateA);
		dateB = Number(dateB);
		if (dateA < dateB) return 1;
		if (dateA > dateB) return -1;
		if (classA < classB) return -1;
		if (classA > classB) return 1;
		return 0;
	});

	// Assign tranche letters (A, B, C, etc.) - same logic as renderUserGiftTransModal
	let letterCount = 0;
	giftTransactions.forEach(function (h) {
		let trancheLetter = numberToLetters(letterCount);
		if (!h.tranche || h.tranche.length > 3) {
			h.tranche = trancheLetter;
			letterCount++;
		}
		if (!h.before) h.before = h.quantity;
	});
	historicHoldings.forEach(function (h) {
		let trancheLetter = numberToLetters(letterCount);
		if (!h.tranche) {
			h.tranche = trancheLetter;
			letterCount++;
		} else if (h.tranche.length > 3) {
			let timeStamp = h.tranche;
			historicHoldings.forEach((k) => {
				if (k.tranche == timeStamp) {
					k.tranche = trancheLetter;
				}
			});
			priorTransfers.forEach((p) => {
				if (p.tranche == timeStamp) p.tranche = trancheLetter;
			});
			letterCount++;
		}
		if (!h.before) h.before = h.quantity;
	});

	return { giftTransactions, historicHoldings, priorTransfers };
}

/**
 * #6602: one holder's rows across the client's classes, as Gift Transfer gathers them. For each class the rows
 * come from the first of scopedTransactionSources that has the class, else clientData.equity[class].transactions.
 * certColumn uses the cached whole-client answer when there is one (#6144-Y), else '#' in this pool's notes.
 */
export function gatherHolderTransactions({ clientData, shareholder, scopedTransactionSources = [], applyTypeFilter = false, cachedCertColumn = null }) {
	let giftTransactions = [];
	const includedClasses = [];
	const classTransactionsByClass = {};
	// #6144e: CERT_COLUMN/CLASS_COLUMN are `||=`-latched module globals that used to
	// never reset outside dataOnly mode -- once any account/class anywhere in the tab
	// set them true, every later shareholder's render (including their matching logic,
	// not just the rendered column) silently inherited that state. Reset fresh per call.
	let CERT_COLUMN = false;
	let CLASS_COLUMN = false;
	// GH: giftTransactions below holds the SAME object references as classTransactions
	// (a plain push, not a clone) -- the matching engine mutates .quantity in place on
	// those objects to represent a partially-consumed lot's remaining balance for
	// display. Snapshot each transaction's real quantity here, before any matcher can
	// touch it, so a later full-record-replace write (e.g. the Tax Basis inline-edit
	// below) can send the true persisted value instead of an analysis-adjusted one.
	const pristineQuantityByNrid = new Map();
	let classes = Object.keys(clientData.equity);
	if (classes.length > 1) CLASS_COLUMN = true;
	classes.sort();

	// CERT_COLUMN is decided solely by '#' in transaction notes (below) --
	// having a certificate template or show_certificates flag doesn't mean a
	// client assigns cert NUMBERS (false-positived for Edmund Optics and others).

	classes.forEach((c, i) => {
		// console.log(clientData.equity[c].transactions)
		let classIncluded = false;
		// Try looking at Transactions first, then Approved balances
		// For EXTERNAL_DATA admin views, scoped transactions are on shareholderData.scopedTransactions
		// rather than clientData.equity[c].transactions (which is re-read from clean sessionStorage here).
		// dataOnly callers scanning many different shareholders in a loop can't rely on that page-global
		// (it's fixed to whichever one shareholder's edit page is actually open) -- options.shareholderData
		// lets them supply their own scopedTransactions (e.g. one bulk fetchScopedTransactions({userids: []})
		// call, reused across every holder in the loop) instead.
		// #6144b: filter by Security Type before anything downstream (matching, tranche building,
		// the sharesOwned headline) ever sees these -- see isTrancheEligibleSecurityType().
		let classTransactions = (scopedTransactionSources.find((s) => s?.[c])?.[c] || clientData.equity[c].transactions || []).filter(
			applyTypeFilter ? isTrancheEligibleSecurityType : () => true
		);
		classTransactionsByClass[c] = classTransactions;
		classTransactions.forEach(function (t) {
			if ((t.receiver && t.receiver.toLowerCase() == shareholder) || (t.sender && t.sender.toLowerCase() == shareholder)) {
				t.shareClass = classes[i];
				if (t.nrid && !pristineQuantityByNrid.has(t.nrid)) pristineQuantityByNrid.set(t.nrid, t.quantity);
				giftTransactions.push(t);
				classIncluded = true;
			}
		});

		// Gift Transfer uses certificate-aware matching as analysis logic,
		// not merely table rendering. Keep CERT_COLUMN as a runtime analysis flag.
		CERT_COLUMN ||= cachedCertColumn !== null ? cachedCertColumn : classTransactions.some((t) => t.notes.includes('#'));

		// Dont't display future (beyond this year) Events or Transactions
		let nextYear = new Date(new Date().getFullYear() + 1, 1, 1) / 1000;
		giftTransactions = giftTransactions.filter((t) => Number(t.date) < nextYear);
		console.log('initialTransactions[' + c + ']: ', giftTransactions);

		if (classIncluded) includedClasses.push(c);
		//clientEvents = clientEvents.concat(clientData.equity[c].events);
	});

	return { classes, giftTransactions, includedClasses, classTransactionsByClass, pristineQuantityByNrid, certColumn: CERT_COLUMN, classColumn: CLASS_COLUMN };
}

/**
 * #6602: a holder's transferable tranches in one class (every class when shareClass is null), for the shared tranche table -- the same rows the
 * Gift Transfer modal offers (non-zero remaining quantity), from the same gather/analyze/letter steps.
 * holder is the holder's cap-table record; scopedTransactions is the client's rows by class (EXTERNAL_DATA).
 */
export function transferableTranches({ clientData, holder, shareClass, scopedTransactions = null, cachedCertColumn = null }) {
	const shareholder = String(holder?.userid || '').toLowerCase();
	const gathered = gatherHolderTransactions({
		clientData,
		shareholder,
		scopedTransactionSources: [scopedTransactions],
		applyTypeFilter: true,
		cachedCertColumn,
	});
	const priorTransfers = [];
	let { giftTransactions, historicHoldings } = analyzeTranches({
		classes: gathered.classes,
		giftTransactions: JSON.parse(JSON.stringify(gathered.giftTransactions)),
		historicHoldings: [],
		priorTransfers,
		giftUserData: holder,
		giftUserId: holder?.userid || shareholder,
		clientData,
		certColumn: gathered.certColumn,
	});
	({ giftTransactions } = letterTranches({ giftTransactions, historicHoldings, priorTransfers }));
	return giftTransactions
		.filter((t) => (shareClass == null || t.shareClass === shareClass) && Number(t.quantity) > 0)
		.map((t) => ({
			ref: t.nrid,
			letter: t.tranche,
			shareClass: t.shareClass,
			cert: t.certNumber || '',
			date: Number(t.date),
			description: t.notes || '',
			quantity: Number(t.quantity),
			basis: isKnownNumber(t._effectiveTaxBasis) ? Number(t._effectiveTaxBasis) : null,
			basisSource: t._basisSource || '',
			confidence: t._matchKey || '',
			eventFunded: !!t._eventFunded,
			certColumn: !!gathered.certColumn,
			// the source record's stored quantity (t.quantity is what's left after matching)
			sourceQuantity: String(gathered.pristineQuantityByNrid.get(t.nrid) ?? t.quantity),
			source: t,
		}));
}

// #6602: a holder's own open tranches for read-only views (Events & Transactions, the dashboard's Tranches tab):
// equity classes only, each row marked with whether its class counts in dollars.
export function holderTranches({ clientData, holder, shareClass = null, cachedCertColumn = null }) {
	return transferableTranches({ clientData, holder, shareClass, scopedTransactions: holder?.scopedTransactions ?? null, cachedCertColumn })
		.filter((t) => !clientData.equity?.[t.shareClass]?.hasOwnProperty('interestRate'))
		.map((t) => ({ ...t, dollars: getEffectiveDenomination(clientData, t.shareClass) === 'dollars' }));
}

// #6602: default selection -- highest tax basis first, oldest first on ties; tranches with unknown basis last.
export function selectTranches(tranches, quantity) {
	const ordered = [...tranches].sort((a, b) => (b.basis ?? -Infinity) - (a.basis ?? -Infinity) || a.date - b.date);
	let left = Number(quantity) || 0;
	const picks = [];
	for (const t of ordered) {
		if (left <= 0) break;
		const take = Math.min(t.quantity, left);
		picks.push({ ref: t.ref, quantity: take });
		left = +(left - take).toFixed(6);
	}
	return picks;
}

// #6602: how a tranche is named on screen and in messages.
export const trancheLabel = (t) => (t.cert ? `Cert ${t.cert}` : t.description || `Tranche ${t.letter}`);

// #6602: errors for a tranche selection ([{ ref, quantity, taxBasis }]) against its tranches and target quantity.
export function validateTrancheSelection(tranches, selection, quantity, { cashSale = false } = {}) {
	const errors = [];
	const byRef = new Map(tranches.map((t) => [t.ref, t]));
	let total = 0;
	(selection || []).forEach((s) => {
		const t = byRef.get(s.ref);
		const label = t ? trancheLabel(t) : 'A selected tranche';
		const amount = Number(s.quantity);
		if (!t) errors.push(`${label} is no longer available.`);
		else if (!(amount > 0)) errors.push(`${label}: enter an amount greater than zero.`);
		else if (amount > t.quantity + 1e-9) errors.push(`${label}: amount exceeds the ${t.quantity} remaining.`);
		if (!cashSale && !isKnownNumber(s.taxBasis)) errors.push(`${label}: tax basis is required.`);
		total += amount > 0 ? amount : 0;
	});
	if (Math.abs(total - Number(quantity)) > 1e-6) errors.push(`Selected tranches total ${+total.toFixed(6)}, but ${Number(quantity)} were requested.`);
	return errors;
}

// ---- Gift custom-column defaults (Sender Basis, next_cert_number, ...) ----

// #6405: Sender Basis is a standard column, but Gift Transfer fills and edits it like a custom one
export function senderBasisGiftColumns(clientData) {
	if (!isSenderBasisEnabled(clientData)) return [];
	const label = new TransactionColumnRegistry(clientData).getLabel('senderBasis');
	return [{ key: 'senderBasis', label, format: 'currency', autoPopulate: 'own_field', autoPopulateField: 'taxBasis' }];
}

// Custom columns for this shareClass, stored flat under col.key on the row.
export function applicableGiftCustomColumns(clientData, shareClass) {
	const custom = listTransactionCustomColumns(clientData)
		.filter(usesTransactionCustomColumnKeyStorage)
		.filter((col) => isCustomColumnApplicableToClass(col, shareClass));
	return senderBasisGiftColumns(clientData).concat(custom);
}

// Suggests a value for every applicable column not already set on `row`.
// sourceTxn: the source tranche this gift consumes, for own_field/source_field.
// fallbackResolvers: named fallbacks a col.autoPopulateFallback can invoke.
// classTransactions: this class's full list (EXTERNAL_DATA only, see
// commitTranche's call site) -- for next_cert_number.
// @returns {{key, value, autoPopulate}[]} columns actually set this call, so the
// caller can log next_cert_number assignments (see customCertLog).
export function applyGiftCustomColumnDefaults(row, clientData, shareClass, dateMs, sourceTxn, fallbackResolvers, classTransactions) {
	const asOfSeconds = dateMs ? Math.floor(Number(dateMs) / 1000) : undefined;
	// Shared across columns so sequential cert fields don't collide (context.certOffset).
	const context = {};
	const assigned = [];
	applicableGiftCustomColumns(clientData, shareClass).forEach((col) => {
		if (row[col.key] !== undefined && row[col.key] !== '') return;
		// Isolate failures per column -- one bad resolver shouldn't blank the rest.
		try {
			const suggested = getCustomColumnAutoPopulateValue(col, clientData, shareClass, asOfSeconds, context, {
				row,
				sourceTxn,
				fallbackResolvers,
				classTransactions,
			});
			if (suggested) {
				row[col.key] = suggested;
				assigned.push({ key: col.key, value: suggested, autoPopulate: col.autoPopulate });
			}
		} catch (err) {
			console.warn('applyGiftCustomColumnDefaults: resolver failed for', col.key, err);
		}
	});
	return assigned;
}

// Parses Sheetz's real historical description grammar (pre-custom-columns) --
// see docs/sheetz-transaction-description-field.md. Always returns the
// receiver's cert, the last one minted in every shape.
export function parseSheetzReceiverCertFromNotes(notesText) {
	const s = String(notesText || '');
	let m = s.match(
		/from\s+#?([A-Za-z]+-\d+[A-Za-z]?)\s+\(now\s+(?:closed|#?[A-Za-z]+-\d+[A-Za-z]?)\)\s+(?:at\s+\$[\d,]+(?:\.\d+)?\s+)?to\s+#?([A-Za-z]+-\d+[A-Za-z]?)/i
	);
	if (m) return m[2];
	m = s.match(/Transfer Received\s+\(#?([A-Za-z]+-\d+[A-Za-z]?)/i);
	if (m) return m[1];
	m = s.match(/Issuance to\s+#?([A-Za-z]+-\d+[A-Za-z]?)/i);
	if (m) return m[1];
	m = s.match(/Stock Certificate Issuance\s+\(#?([A-Za-z]+-\d+[A-Za-z]?)\)/i);
	if (m) return m[1];
	return '';
}

// Flat {key: value} snapshot for buildTransactionRecord()'s customColumns
// param -- mirrors ledgers.js's own customColumnSnapshot().
export function giftCustomColumnSnapshot(row, clientData) {
	const out = {};
	senderBasisGiftColumns(clientData)
		.concat(listTransactionCustomColumns(clientData))
		.forEach((col) => {
			if (row[col.key] !== undefined) out[col.key] = row[col.key];
		});
	return out;
}
